/**
 * ForecastingDashboard1 - Dual Dashboard with Maestro and Forecasting views
 */

import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { List, useListRef } from 'react-window';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MentionInput from '../common/MentionInput';
import { FilterIcon, FilterTemplateDropdown, useFilterTemplate } from '../common/FilteringTemplate';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import { ListenButton } from '../common/AudioGuide';
import { formatMaestroBatchOption } from './maestroBatchLabel';
import HelpVideoTour from '../Help/HelpVideoTour';
import { DISCIPLINES as CODING_DISCIPLINES } from '../../data/codingStructureData';
import useColumnOrder from '../../hooks/useColumnOrder';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import AuditLogModal from '../common/AuditLogModal';
import { applyColumnOrder } from '../../utils/columnLayout';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';

// Column definitions for Forecasting Dashboard
// Column groups with light pastel colors and black text
const COLUMN_GROUPS = [
  { id: 'identification', label: 'Identification', color: '#d4edda', textColor: '#1f2937', columns: ['contract_vs_extra', 'operation_code', 'cost_type', 'activity'] },  // light green
  { id: 'budget', label: 'Budget', color: '#fff3cd', textColor: '#1f2937', columns: ['orig_bud', 'revised_bud'] },  // light creme/yellow
  { id: 'actual', label: 'Actual', color: '#f8d7da', textColor: '#1f2937', columns: ['actual'] },  // light pink
  { id: 'committed', label: 'Committed', color: '#f8d7da', textColor: '#1f2937', columns: ['comm_costs'] },  // light pink
  { id: 'variance', label: 'Variance', color: '#f8d7da', textColor: '#1f2937', columns: ['bud_bal'] },  // light pink
  { id: 'forecast', label: 'Forecast', color: '#cce5ff', textColor: '#1f2937', columns: ['percent_complete', 'percent_complete_prev', 'etc_estimate_to_complete', 'etc_prev', 'fac_forecast_at_completion', 'fac_prev', 'revised_selling_bud', 'revised_selling_bud_prev', 'estimated_profit_at_completion', 'percent_margin', 'fore_var_dollars', 'fore_var_percent'] },  // light sky blue
  { id: 'notes', label: 'Notes', color: '#e9ecef', textColor: '#1f2937', columns: ['note'] },  // light gray
];

const FORECASTING_COLUMNS = [
  // Identification group - mix of calculated (cream) and Maestro data (gray)
  { key: 'contract_vs_extra', label: 'Contract vs Extra', type: 'text', width: 120, group: 'identification', frozen: true, readOnly: true }, // From Maestro via operation code - gray
  { key: 'operation_code', label: 'Operation Code', type: 'text', width: 120, group: 'identification', frozen: true, readOnly: true }, // From Maestro LEFT(Activity,5) - gray
  { key: 'cost_type', label: 'Cost Type', type: 'text', width: 100, group: 'identification', frozen: true, readOnly: true }, // From Maestro (phase, first 6 chars removed) - gray
  { key: 'activity', label: 'Activity', type: 'text', width: 200, group: 'identification', frozen: true, readOnly: true }, // Maestro data - gray
  // Budget group - Maestro data (light gray)
  { key: 'orig_bud', label: 'Orig Bud', type: 'number', width: 100, group: 'budget', readOnly: true },
  { key: 'revised_bud', label: 'Revised Bud', type: 'number', width: 100, group: 'budget', readOnly: true }, // Maestro data - gray
  // Actual group - Maestro data (light gray)
  { key: 'actual', label: 'Actual', type: 'number', width: 100, group: 'actual', readOnly: true },
  // Committed group - Maestro data (light gray)
  { key: 'comm_costs', label: 'Comm Costs', type: 'number', width: 100, group: 'committed', readOnly: true },
  // Variance group - Maestro data (light gray)
  { key: 'bud_bal', label: 'Bud Bal', type: 'number', width: 100, group: 'variance', readOnly: true },
  // Forecast group - mix of manual entry (light green), previous data (light blue), and calculated (light cream)
  { key: 'percent_complete', label: '% Complete', type: 'percent', width: 100, group: 'forecast' }, // manual entry - light green
  { key: 'percent_complete_prev', label: '% Complete Prev', type: 'percent', width: 110, group: 'forecast', readOnly: true, previous: true }, // previous data - light blue
  { key: 'etc_estimate_to_complete', label: 'ETC (Est. Balance to Complete)', type: 'number', width: 160, group: 'forecast' }, // manual entry - light green
  { key: 'etc_prev', label: 'ETC Prev', type: 'number', width: 100, group: 'forecast', readOnly: true, previous: true }, // previous data - light blue
  { key: 'fac_forecast_at_completion', label: 'FAC (Est. Cost to Completion)', type: 'number', width: 170, group: 'forecast', readOnly: true, calculated: true }, // calculated - light cream
  { key: 'fac_prev', label: 'FAC Prev', type: 'number', width: 100, group: 'forecast', readOnly: true, previous: true }, // previous data - light blue
  { key: 'revised_selling_bud', label: 'Revised Selling Bud', type: 'number', width: 140, group: 'forecast' }, // manual entry - light green
  { key: 'revised_selling_bud_prev', label: 'Revised Selling Bud Prev', type: 'number', width: 150, group: 'forecast', readOnly: true, previous: true }, // previous data - light blue
  { key: 'estimated_profit_at_completion', label: 'Forecasted Profit (Est. Profit at Completion)', type: 'number', width: 200, group: 'forecast', readOnly: true, calculated: true }, // calculated - light cream
  { key: 'percent_margin', label: '% Margin', type: 'percent', width: 100, group: 'forecast', readOnly: true, calculated: true }, // calculated - light cream
  { key: 'fore_var_dollars', label: 'Fore Var $', type: 'number', width: 110, group: 'forecast', readOnly: true, calculated: true }, // calculated - light cream
  { key: 'fore_var_percent', label: 'Fore Var %', type: 'percent', width: 100, group: 'forecast', readOnly: true, calculated: true }, // calculated - light cream
  // Notes group - manual entry (light green)
  { key: 'note', label: 'Note', type: 'text', width: 200, group: 'notes' },
];

// ALL columns from maestro_import table
const MAESTRO_COLUMNS = [
  { key: 'phase', label: 'Phase', type: 'text', width: 100 },
  { key: 'activity', label: 'Activity', type: 'text', width: 200 },
  { key: 'group_col', label: 'Group', type: 'text', width: 120 },
  { key: 'orig_bud', label: 'Orig Bud', type: 'number', width: 100 },
  { key: 'projection_q', label: 'Projection(Q)', type: 'number', width: 110 },
  { key: 'to_complete_q', label: 'To Complete(Q)', type: 'number', width: 120 },
  { key: 'tot_bud_bal', label: 'Tot Bud Bal', type: 'number', width: 100 },
  { key: 'bud_bal_q', label: 'Bud Bal(Q)', type: 'number', width: 100 },
  { key: 'pct_budget_spent', label: '%Budget Spent', type: 'percent', width: 110 },
  { key: 'accrued_q', label: 'Accrued(Q)', type: 'number', width: 100 },
  { key: 'production', label: 'Production', type: 'number', width: 100 },
  { key: 'po_total_q', label: 'PO Total(Q)', type: 'number', width: 100 },
  { key: 'rev_qty_to_fulfill', label: 'Rev. Qty to FulFill', type: 'number', width: 130 },
  { key: 'production_q', label: 'Production(Q)', type: 'number', width: 110 },
  { key: 'contingency', label: 'Contingency', type: 'number', width: 100 },
  { key: 'billing_q', label: 'Billing(Q)', type: 'number', width: 100 },
  { key: 'mod_qty_to_fulfill', label: 'Mod. Qty to FulFill', type: 'number', width: 130 },
  { key: 'orig_qty_to_fulfill', label: 'Orig. Qty to FulFill', type: 'number', width: 130 },
  { key: 'contingency_q', label: 'Contingency(Q)', type: 'number', width: 120 },
  { key: 'projection', label: 'Projection', type: 'number', width: 100 },
  { key: 'billing', label: 'Billing', type: 'number', width: 100 },
  { key: 'to_complete', label: 'To Complete', type: 'number', width: 100 },
  { key: 'revised_selling_bud_q', label: 'Revised Selling Bud(Q)', type: 'number', width: 150 },
  { key: 'selling_bud_modif_q', label: 'Selling Bud Modif(Q)', type: 'number', width: 140 },
  { key: 'bud_modif', label: 'Bud Modif', type: 'number', width: 100 },
  { key: 'accrued', label: 'Accrued', type: 'number', width: 100 },
  { key: 'orig_selling_bud_q', label: 'Orig Selling Bud(Q)', type: 'number', width: 140 },
  { key: 'selling_bud_modif', label: 'Selling Bud Modif', type: 'number', width: 130 },
  { key: 'orig_selling_bud', label: 'Orig Selling Bud', type: 'number', width: 120 },
  { key: 'pct_budget_spent_tot_q', label: '%Budget Spent Tot(Q)', type: 'percent', width: 140 },
  { key: 'revised_bud', label: 'Revised Bud', type: 'number', width: 100 },
  { key: 'pct_budget_spent_tot', label: '%Budget Spent Tot', type: 'percent', width: 130 },
  { key: 'actual', label: 'Actual', type: 'number', width: 100 },
  { key: 'total_and_po_q', label: 'Actual Total & PO(Q)', type: 'number', width: 140 },
  { key: 'tot_bud_bal_q', label: 'Tot Bud Bal(Q)', type: 'number', width: 110 },
  { key: 'po_bal', label: 'PO Bal', type: 'number', width: 100 },
  { key: 'total_and_po', label: 'Total & PO', type: 'number', width: 100 },
  { key: 'po_bal_q', label: 'PO Bal(Q)', type: 'number', width: 100 },
  { key: 'po_inv_q', label: 'PO Inv(Q)', type: 'number', width: 100 },
  { key: 'pct_budget_spent_q', label: '%Budget Spent(Q)', type: 'percent', width: 130 },
  { key: 'po_total', label: 'PO Total', type: 'number', width: 100 },
  { key: 'po_inv', label: 'PO Inv', type: 'number', width: 100 },
  { key: 'actual_plus_accrued', label: 'Actual+Accrued', type: 'number', width: 120 },
  { key: 'comm_costs', label: 'Comm Costs', type: 'number', width: 100 },
  { key: 'bud_bal', label: 'Bud Bal', type: 'number', width: 100 },
  { key: 'bud_modif_q', label: 'Bud Modif(Q)', type: 'number', width: 110 },
  { key: 'orig_bud_q', label: 'Orig Bud(Q)', type: 'number', width: 100 },
  { key: 'actual_plus_accrued_q', label: 'Actual+Accrued(Q)', type: 'number', width: 140 },
  { key: 'actual_q', label: 'Actual(Q)', type: 'number', width: 100 },
  { key: 'revised_bud_q', label: 'Revised Bud(Q)', type: 'number', width: 120 },
  { key: 'revised_selling_bud', label: 'Revised Selling Bud', type: 'number', width: 140 },
];

// ─── react-window virtualization constants ────────────────────────────────────
const FD_ROW_HEIGHT = 36;       // px — matches cell padding (8px×2) + 13px line-height
const FD_TABLE_MAX_HEIGHT = 495; // px — matches existing maxHeight
const FD_FILLABLE_FIELDS = new Set(['percent_complete', 'revised_selling_bud']);
const FD_FILL_AUTO_SCROLL_ZONE = 50;
const FD_FILL_AUTO_SCROLL_SPEED = 6;

// ─── default sorting per view + per-user persistence ──────────────────────────
// Forecasting opens sorted by Operation Code, Maestro by Activity. When the
// user changes the sort it is remembered per view in localStorage (same
// per-user-per-browser mechanism as the Columns personalization).
const FD_SORT_DEFAULTS = {
  forecasting: { key: 'operation_code', direction: 'asc' },
  maestro: { key: 'activity', direction: 'asc' },
};
const fdSortStorageKey = (view) => `${view}-dashboard-sort`;
const loadStoredSort = (view) => {
  try {
    const saved = JSON.parse(localStorage.getItem(fdSortStorageKey(view)));
    if (saved && saved.key && (saved.direction === 'asc' || saved.direction === 'desc')) return saved;
  } catch (_) { /* corrupted value — fall through to default */ }
  return FD_SORT_DEFAULTS[view] || { key: null, direction: 'asc' };
};

// Row comparator for the active sort — shared by the on-screen table and the
// Excel export (so an "All rows" export is ordered the same way as the screen).
const makeSortComparator = (sortConfig) => (a, b) => {
  const aVal = a[sortConfig.key];
  const bVal = b[sortConfig.key];

  // Handle null/undefined
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
  if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

  // Numeric comparison
  const aNum = parseFloat(aVal);
  const bNum = parseFloat(bVal);
  if (!isNaN(aNum) && !isNaN(bNum)) {
    return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
  }

  // String comparison
  const comparison = String(aVal).localeCompare(String(bVal));
  return sortConfig.direction === 'asc' ? comparison : -comparison;
};

// Module-level format helpers used by FDRowComponent
const _fdFormatMentions = (text) => {
  if (!text || typeof text !== 'string') return text;
  const mentionRegex = /(@[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,})/g;
  const parts = text.split(mentionRegex);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (part.match(mentionRegex)) {
      return (
        <span key={i} style={{ color: '#0066cc', fontWeight: '600', backgroundColor: '#e7f3ff', padding: '0 3px', borderRadius: '3px' }} title={`Mentioned: ${part.slice(1)}`}>
          {part}
        </span>
      );
    }
    return part;
  });
};

const _fdFormatValue = (value, type, colKey) => {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'number') return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (type === 'percent') return `${Number(value).toFixed(1)}%`;
  if (colKey === 'note') return _fdFormatMentions(value);
  return value;
};

// Display still shows 0 / 0.0%, but the editor opens blank so the user can type immediately.
const _fdInitialEditValue = (raw, type) => {
  if (type === 'number' || type === 'percent') {
    if (raw === null || raw === undefined || raw === '') return '';
    const n = Number(raw);
    if (!Number.isFinite(n) || n === 0) return '';
    return type === 'percent' ? parseFloat(n.toFixed(1)) : raw;
  }
  return raw ?? '';
};

// Virtualized row — rendered by react-window v2 List
const FDRowComponent = React.memo(function FDRowComponent({
  index, style,
  filteredData, visibleCols, frozenOffsets, columnWidths, totalWidth,
  selectedRows, setSelectedRows,
  editingCell, editValue, setEditingCell, setEditValue, handleCellUpdate,
  recentlyModified,
  fillDrag, onFillStart,
  readOnlyAll,
}) {
  const row = filteredData[index];
  if (!row) return null;
  const isEvenRow = index % 2 === 0;
  const isSelected = selectedRows.has(row.id);
  const rowBg = isSelected ? '#fef2f2' : isEvenRow ? 'white' : '#f9fafb';
  const isEditingRow = editingCell?.rowId === row.id;

  return (
    <div
      data-fd-row-index={index}
      style={{
        ...style,
        width: totalWidth,
        display: 'flex',
        boxSizing: 'border-box',
        zIndex: isEditingRow ? 10 : 1,
        backgroundColor: rowBg,
        userSelect: fillDrag?.active ? 'none' : undefined,
      }}
    >
      {/* Checkbox cell */}
      <div style={{
        width: 40, minWidth: 40, maxWidth: 40, flexShrink: 0,
        padding: '8px',
        borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb',
        position: 'sticky', left: 0,
        backgroundColor: rowBg,
        zIndex: 5,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxSizing: 'border-box',
      }}>
        <input type="checkbox" checked={isSelected} onChange={() => {
          setSelectedRows(prev => { const next = new Set(prev); if (next.has(row.id)) next.delete(row.id); else next.add(row.id); return next; });
        }} />
      </div>
      {/* Data cells */}
      {visibleCols.map((col) => {
        const colWidth = columnWidths[col.key] || col.width;
        const frozenLeft = frozenOffsets[col.key];
        const modifiedKey = `${row.id}-${col.key}`;
        const isRecentlyModified = recentlyModified[modifiedKey] !== undefined;
        const fillLo = fillDrag?.active ? Math.min(fillDrag.startRow, fillDrag.currentRow) : -1;
        const fillHi = fillDrag?.active ? Math.max(fillDrag.startRow, fillDrag.currentRow) : -1;
        const isInFillRange = fillDrag?.active && fillDrag.field === col.key && index >= fillLo && index <= fillHi;
        let cellBg;
        if (isInFillRange) {
          cellBg = '#93c5fd';
        } else if (isRecentlyModified) {
          cellBg = '#86efac';
        } else if (isSelected) {
          cellBg = '#fef2f2';
        } else if (col.calculated) {
          cellBg = isEvenRow ? '#fef9c3' : '#fef3c7';
        } else if (col.previous) {
          cellBg = isEvenRow ? '#e0f2fe' : '#dbeafe';
        } else if (col.readOnly) {
          cellBg = isEvenRow ? '#f3f4f6' : '#e5e7eb';
        } else {
          cellBg = isEvenRow ? '#dcfce7' : '#d1fae5';
        }
        const isBlockedEdit = col.key === 'revised_selling_bud' && (row.contract_vs_extra || '').toLowerCase() === 'contract';
        const isEditing = editingCell?.rowId === row.id && editingCell?.field === col.key;
        const showFillHandle = FD_FILLABLE_FIELDS.has(col.key) && !col.readOnly && !isBlockedEdit && !isEditing && onFillStart && !readOnlyAll;
        // Driver highlighting — % Complete and ETC are linked: entering one
        // recalculates the other. Bold the one the user actually entered (the
        // "driver") so it is obvious which value is driving the forecast.
        //   etc_override === true → ETC was typed manually (% Complete derived)
        //   otherwise             → % Complete drives whenever it is > 0 (ETC derived)
        const isDriverCell =
          (col.key === 'etc_estimate_to_complete' && row.etc_override === true) ||
          (col.key === 'percent_complete' && row.etc_override !== true && (Number(row.percent_complete) || 0) > 0);
        return (
          <div
            key={col.key}
            style={{
              width: colWidth, minWidth: colWidth, maxWidth: col.frozen ? colWidth : undefined,
              flexShrink: 0,
              padding: '8px',
              borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb',
              cursor: (col.readOnly || isBlockedEdit) ? 'default' : 'pointer',
              position: col.frozen ? 'sticky' : 'relative',
              left: frozenLeft,
              backgroundColor: cellBg,
              zIndex: col.frozen ? 5 : 1,
              transition: 'background-color 0.3s ease',
              boxSizing: 'border-box',
              overflow: isEditing ? 'visible' : 'hidden',
            }}
            onClick={() => {
              if (col.readOnly || isEditing) return;
              if (readOnlyAll) {
                alert('Editing is disabled while viewing converted currency.\nSwitch View $ back to the native currency to edit.');
                return;
              }
              if (isBlockedEdit) {
                alert('Cannot override Revised Selling Budget for Contract type rows.\nOnly Extra type rows can be overridden.');
                return;
              }
              setEditingCell({ rowId: row.id, field: col.key });
              setEditValue(_fdInitialEditValue(row[col.key], col.type));
            }}
            title={isBlockedEdit
              ? 'Override blocked for Contract rows'
              : (isRecentlyModified
                ? 'Recently modified'
                : (isDriverCell
                  ? 'Driver — this value was entered by a user; the linked value (% Complete ↔ ETC) was calculated from it'
                  : ''))}
          >
            {isEditing ? (
              col.key === 'note' ? (
                <MentionInput
                  value={editValue}
                  onChange={(val) => setEditValue(val)}
                  onBlur={() => handleCellUpdate(row.id, col.key, editValue)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditingCell(null);
                    if (e.key === 'Enter' && !e.shiftKey && e.target.tagName !== 'TEXTAREA') {
                      handleCellUpdate(row.id, col.key, editValue);
                    }
                  }}
                  autoFocus
                  rows={2}
                  placeholder="Add note... Use @ to mention someone"
                  style={{ width: '100%', minWidth: '200px', padding: '4px 8px', border: '2px solid #dc3545', borderRadius: '4px', fontSize: '13px' }}
                />
              ) : (
                <input
                  type={col.type === 'number' || col.type === 'percent' ? 'number' : 'text'}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onBlur={() => handleCellUpdate(row.id, col.key, editValue)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCellUpdate(row.id, col.key, editValue); else if (e.key === 'Escape') setEditingCell(null); }}
                  autoFocus
                  style={{ width: '100%', padding: '4px 8px', border: '2px solid #dc3545', borderRadius: '4px', fontSize: '13px' }}
                />
              )
            ) : (
              <span style={{
                color: (col.type === 'number' || col.type === 'percent') && Number(row[col.key]) < 0 ? '#dc2626' : '#111827',
                display: 'block',
                fontWeight: isDriverCell
                  ? '700'
                  : ((col.type === 'number' || col.type === 'percent') && Number(row[col.key]) < 0 ? '600' : 'normal'),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {_fdFormatValue(row[col.key], col.type, col.key)}
              </span>
            )}
            {showFillHandle && (
              <div
                data-fill-handle="1"
                onMouseDown={(e) => onFillStart(e, index, col.key, row[col.key])}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                title="Drag to fill cells"
                style={{
                  position: 'absolute',
                  bottom: 2,
                  right: 2,
                  width: 7,
                  height: 7,
                  backgroundColor: '#007bff',
                  cursor: 'crosshair',
                  borderRadius: 1,
                  zIndex: 10,
                  boxShadow: '0 0 1px rgba(0,0,0,0.3)',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
});
// ─── end react-window virtualization ─────────────────────────────────────────

const ForecastingDashboard1 = ({ jobNumber }) => {
  const { instance, accounts } = useMsal();

  // Live data push support
  const fetchDataRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchDataRef.current) fetchDataRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('forecastingDashboard', jobNumber || 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // State
  const [dashboardView, setDashboardView] = useState('forecasting');
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [forecastingData, setForecastingData] = useState([]);
  const [maestroData, setMaestroData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [forecastingVisibleColumns, setForecastingVisibleColumns] = useState(() => new Set(FORECASTING_COLUMNS.map(c => c.key)));
  const [maestroVisibleColumns, setMaestroVisibleColumns] = useState(() => new Set(MAESTRO_COLUMNS.map(c => c.key)));
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const forecastingColOrder = useColumnOrder('forecasting-dashboard-column-order', FORECASTING_COLUMNS);
  const maestroColOrder = useColumnOrder('maestro-dashboard-column-order', MAESTRO_COLUMNS);
  const [importDataLoading, setImportDataLoading] = useState(false);
  // importDataModal: null | { type: 'confirm-unfinalized'|'confirm-replace'|'confirm-fresh'|'success'|'error', message? }
  const [importDataModal, setImportDataModal] = useState(null);
  // Maestro Reports batches offered in the Import Data modal (newest first)
  const [importBatches, setImportBatches] = useState([]);
  const [importSelectedBatchId, setImportSelectedBatchId] = useState(null);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  // Export panel choices: rows filtered-vs-complete, columns visible-vs-all
  const [exportRowsScope, setExportRowsScope] = useState('filtered');
  const [exportColsScope, setExportColsScope] = useState('visible');
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [showAnalyticsEmailModal, setShowAnalyticsEmailModal] = useState(false);
  const [analyticsEmailAddresses, setAnalyticsEmailAddresses] = useState('');
  const [analyticsEmailSending, setAnalyticsEmailSending] = useState(false);
  const [analyticsEmailResult, setAnalyticsEmailResult] = useState(null);
  const [analyticsExporting, setAnalyticsExporting] = useState(null); // 'pdf' | 'excel' | null
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpTab, setHelpTab] = useState('guide'); // 'guide' or 'flowchart'
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [finalizationHistory, setFinalizationHistory] = useState([]);
  const [finalizeStep, setFinalizeStep] = useState(1); // 1 = copy values question, 2 = PDF question
  const [evmHistory, setEvmHistory] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editingCellRef = useRef(null);
  const updateEditingCell = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(editingCellRef.current) : next;
    editingCellRef.current = resolved;
    setEditingCell(resolved);
  }, []);
  const clearEditingIfStill = useCallback((rowId, field) => {
    const cur = editingCellRef.current;
    if (cur && cur.rowId === rowId && cur.field === field) {
      editingCellRef.current = null;
      setEditingCell(null);
    }
  }, []);

  const [fillDrag, setFillDrag] = useState({ active: false, startRow: null, currentRow: null, field: null, value: null });
  const fillDragRef = useRef(fillDrag);
  useEffect(() => { fillDragRef.current = fillDrag; }, [fillDrag]);
  const fillAutoScrollRef = useRef(null);
  const filteredDataRef = useRef([]);
  const listScrollRef = useRef(null);
  
  // Track recently modified cells for 5-second highlight
  // Format: { [rowId]: { field: 'fieldName', timestamp: Date.now() } }
  const [recentlyModified, setRecentlyModified] = useState({});
  
  // Column width state for resizable columns
  const [columnWidths, setColumnWidths] = useState(() => {
    const widths = {};
    FORECASTING_COLUMNS.forEach(c => { widths[c.key] = c.width; });
    MAESTRO_COLUMNS.forEach(c => { widths[c.key] = c.width; });
    return widths;
  });
  const [subtotalMode, setSubtotalMode] = useState(new Set(['overall'])); // Set of 'overall' | 'byDiscipline'
  const resizingRef = useRef(null);
  // react-window header/list refs for horizontal scroll sync
  const fdHeaderRef = useRef(null);
  const fdSubtotalRef = useRef(null);
  const fdListRef = useListRef();

  // Auto-fit column widths based on content
  const autoFitColumns = useCallback((data, columns) => {
    if (!data || data.length === 0) return;
    
    const newWidths = {};
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    
    columns.forEach(col => {
      // Start with header label width
      let maxWidth = ctx.measureText(col.label).width + 50; // +50 for padding, sort icon, filter icon
      
      // Check content of each row (sample first 100 rows for performance)
      const sampleSize = Math.min(data.length, 100);
      for (let i = 0; i < sampleSize; i++) {
        const value = data[i][col.key];
        if (value !== null && value !== undefined) {
          let displayValue = String(value);
          // Format numbers with commas for accurate measurement
          if (col.type === 'number' && typeof value === 'number') {
            displayValue = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
          const textWidth = ctx.measureText(displayValue).width + 24; // +24 for cell padding
          maxWidth = Math.max(maxWidth, textWidth);
        }
      }
      
      // Clamp between min (60) and max (400) widths
      newWidths[col.key] = Math.max(60, Math.min(400, Math.ceil(maxWidth)));
    });
    
    setColumnWidths(prev => ({ ...prev, ...newWidths }));
  }, []);
  
  // Sorting state — default per view, remembered per user (localStorage)
  const [sortConfig, setSortConfig] = useState(() => loadStoredSort('forecasting'));
  
  // Toast notification state for @mention emails
  const [toast, setToast] = useState(null);

  // Get current columns and data based on view
  const COLUMNS = dashboardView === 'maestro' ? MAESTRO_COLUMNS : FORECASTING_COLUMNS;
  const rawData = dashboardView === 'maestro' ? maestroData : forecastingData;
  const setData = dashboardView === 'maestro' ? setMaestroData : setForecastingData;
  const currentVisibleColumns = dashboardView === 'maestro' ? maestroVisibleColumns : forecastingVisibleColumns;
  const setCurrentVisibleColumns = dashboardView === 'maestro' ? setMaestroVisibleColumns : setForecastingVisibleColumns;
  const apiEndpoint = dashboardView === 'maestro' ? 'maestro-import' : 'forecasting-dashboard';

  // ── Currency context (master currency, rates, import FX stamp) ──
  const [masterCurrency, setMasterCurrency] = useState(null);
  const [fdExchangeRates, setFdExchangeRates] = useState([]);
  const [fxMeta, setFxMeta] = useState(null);
  // View $ toggle — 'native' shows values as stored (master project currency);
  // picking the other currency converts every dollar column for display only.
  const [fdCurrencyView, setFdCurrencyView] = useState('native');

  // Fetch data for both dashboards
  const fetchData = useCallback(async () => {
    if (!jobNumber) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [forecastingRes, maestroRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/forecasting-dashboard/${jobNumber}`),
        authenticatedApiCall(instance, accounts, 'GET', `/maestro-import/${jobNumber}`),
      ]);
      
      if (forecastingRes.data.success) {
        setForecastingData(forecastingRes.data.data);
        setMasterCurrency(forecastingRes.data.master_currency || null);
        setFdExchangeRates(forecastingRes.data.exchange_rates || []);
        setFxMeta(forecastingRes.data.fx_meta || null);
      }
      if (maestroRes.data.success) setMaestroData(maestroRes.data.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  // The "other" currency the toggle can switch to (CAD↔USD only)
  const otherCurrency = masterCurrency === 'USD' ? 'CAD' : masterCurrency === 'CAD' ? 'USD' : null;

  // Rate for the active converted view (master → other)
  const fdActiveConversion = useMemo(() => {
    if (fdCurrencyView === 'native' || !masterCurrency || !otherCurrency) return null;
    const r = fdExchangeRates.find(x => x.from_currency === masterCurrency && x.to_currency === otherCurrency);
    if (!r || !isFinite(parseFloat(r.rate))) return null;
    return { from: masterCurrency, to: otherCurrency, rate: parseFloat(r.rate), updatedAt: r.updated_at };
  }, [fdCurrencyView, masterCurrency, otherCurrency, fdExchangeRates]);

  // Converted-view flag: display-only conversion is active → editing is locked
  const fdConvertedView = dashboardView === 'forecasting' && !!fdActiveConversion;

  // Apply the display conversion to every dollar (number-type) column.
  // Percent columns are ratios and stay untouched. Editing is disabled while
  // this view is active so typed values are never misinterpreted.
  const data = useMemo(() => {
    if (!fdConvertedView) return rawData;
    const dollarKeys = FORECASTING_COLUMNS.filter(c => c.type === 'number').map(c => c.key);
    const rate = fdActiveConversion.rate;
    return rawData.map(row => {
      const out = { ...row };
      for (const key of dollarKeys) {
        const v = out[key];
        if (v === null || v === undefined || v === '') continue;
        const n = parseFloat(v);
        if (!isNaN(n)) out[key] = n * rate;
      }
      return out;
    });
  }, [rawData, fdConvertedView, fdActiveConversion]);

  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When switching between the Forecasting and Maestro views, load that view's
  // remembered sort (or its default: Operation Code / Activity).
  useEffect(() => {
    setSortConfig(loadStoredSort(dashboardView));
  }, [dashboardView]);

  // Handle sorting — persists the choice per view so the portal remembers it
  const handleSort = (key) => {
    setSortConfig(prev => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      };
      try { localStorage.setItem(fdSortStorageKey(dashboardView), JSON.stringify(next)); } catch (_) { /* storage full/blocked */ }
      return next;
    });
  };

  // Auto-calculate Contract vs Extra based on operation code using the coding structure.
  // Labour ranges (21xxx-29xxx) = contract, Extras ranges (81xxx-89xxx) = extra,
  // Overhead (00xxx-02xxx) = contract, Revenue (90xxx) = revenue.
  const getContractVsExtra = (opCode) => {
    const code = parseInt(opCode, 10);
    if (isNaN(code)) return '';
    const prefix = Math.floor(code / 1000);

    if (prefix === 90) return 'revenue';

    for (const d of CODING_DISCIPLINES) {
      const extrasPrefix = parseInt(d.extrasCode, 10) / 1000;
      if (prefix === extrasPrefix) return 'extra';
    }

    if (prefix >= 0 && prefix <= 29) return 'contract';
    return '';
  };

  // Map an operation code to its discipline name using the coding structure.
  // Labour (21-29) and Extras (81-89) for the same discipline group together.
  const getDisciplineForCode = (opCode) => {
    const code = parseInt(opCode, 10);
    if (isNaN(code)) return 'Other';
    const prefix = Math.floor(code / 1000);

    for (const d of CODING_DISCIPLINES) {
      const labourPrefix = parseInt(d.labourRange.split('-')[0], 10) / 1000;
      const extrasPrefix = parseInt(d.extrasCode, 10) / 1000;
      if (prefix === labourPrefix || prefix === extrasPrefix) return d.name;
    }

    if (prefix === 0) return 'Contract Expenses';
    if (prefix === 1) return 'Indirect Labour';
    if (prefix === 2) return 'Direct Labour';
    if (prefix === 90) return 'Revenue';
    return 'Other';
  };

  // Compute calculated fields dynamically
  // Priority Formula Sequence (from Help modal):
  // 1. ETC: If % Complete > 0: ETC = FAC - Comm Costs, If % Complete = 0: Manual entry required
  // 2. FAC: If % Complete > 0: FAC = Comm Costs / % Complete;
  //       If % Complete = 0 and ETC > 0: FAC = Comm Costs + ETC;
  //       If neither: FAC = Orig Bud (original estimated cost)
  // 3. ETC Override: If user manually overrides ETC: FAC = Comm Costs + ETC, % Complete = Comm Costs / FAC
  // 4. Contract items: Forecasted Profit = 0, % Margin = 0
  const computedData = useMemo(() => {
    return data.map(row => {
      const origBud = parseFloat(row.orig_bud) || 0;
      const etc = parseFloat(row.etc_estimate_to_complete) || 0;
      const commCosts = parseFloat(row.comm_costs) || 0;
      const percentComplete = parseFloat(row.percent_complete) || 0;
      const revisedSellingBud = parseFloat(row.revised_selling_bud) || 0;
      
      // Auto-calculate Contract vs Extra from operation code
      // Use calculated value if available, otherwise fall back to database value
      const calculatedContractVsExtra = getContractVsExtra(row.operation_code);
      const contractVsExtra = calculatedContractVsExtra || row.contract_vs_extra || '';
      
      // Calculate FAC based on priority rules:
      // If % Complete > 0: FAC = Comm Costs / (% Complete / 100)
      // If % Complete = 0 and ETC > 0: FAC = Comm Costs + ETC
      // If neither entered: FAC = Orig Bud
      let calculatedFac;
      if (percentComplete > 0) {
        calculatedFac = commCosts / (percentComplete / 100);
      } else if (etc > 0) {
        calculatedFac = commCosts + etc;
      } else {
        calculatedFac = origBud;
      }
      
      // Handle edge cases
      if (!isFinite(calculatedFac) || isNaN(calculatedFac)) {
        calculatedFac = etc > 0 ? (commCosts + etc) : origBud;
      }
      
      // Forecasted Profit and % Margin:
      // IF contract = "contract": Forecasted Profit = 0, % Margin = 0
      // Otherwise: Forecasted Profit = Revised Selling Bud - FAC
      let forecastedProfit;
      let percentMargin;
      
      if (contractVsExtra === 'contract') {
        forecastedProfit = 0;
        percentMargin = 0;
      } else {
        forecastedProfit = revisedSellingBud - calculatedFac;
        percentMargin = calculatedFac !== 0 ? (forecastedProfit / calculatedFac) * 100 : 0;
      }
      
      // Fore Var $ = Orig Bud - FAC
      const foreVarDollars = origBud - calculatedFac;
      
      // Fore Var % = (Orig Bud - FAC) / Orig Bud
      const foreVarPercent = origBud !== 0 ? ((origBud - calculatedFac) / origBud) * 100 : 0;
      
      return {
        ...row,
        contract_vs_extra: contractVsExtra,
        fac_forecast_at_completion: calculatedFac,
        estimated_profit_at_completion: forecastedProfit,
        percent_margin: percentMargin,
        fore_var_dollars: foreVarDollars,
        fore_var_percent: foreVarPercent,
      };
    });
  }, [data]);

  // Filtering Template (reusable module from Selected Contractors)
  const filterTemplate = useFilterTemplate({ items: computedData, columns: COLUMNS });

  const activeFilters = filterTemplate.getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0 || searchTerm.trim();

  const filteredData = useMemo(() => {
    let result = filterTemplate.filteredItems;

    // Apply search term filter (local to this dashboard)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(row => Object.values(row).some(val => String(val).toLowerCase().includes(search)));
    }

    // Apply sorting
    if (sortConfig.key) {
      result = [...result].sort(makeSortComparator(sortConfig));
    }
    
    return result;
  }, [filterTemplate.filteredItems, searchTerm, sortConfig]);

  useEffect(() => { filteredDataRef.current = filteredData; }, [filteredData]);

  const handleFillStart = useCallback((e, rowIndex, field, value) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setFillDrag({ active: true, startRow: rowIndex, currentRow: rowIndex, field, value });
  }, []);

  const applyFillRange = useCallback(async () => {
    const drag = fillDragRef.current;
    if (!drag?.active) return;
    if (fillAutoScrollRef.current) {
      cancelAnimationFrame(fillAutoScrollRef.current);
      fillAutoScrollRef.current = null;
    }

    const { startRow, currentRow, field, value } = drag;
    setFillDrag({ active: false, startRow: null, currentRow: null, field: null, value: null });

    if (startRow == null || currentRow == null || startRow === currentRow) return;
    if (!FD_FILLABLE_FIELDS.has(field) || !jobNumber) return;

    const rows = filteredDataRef.current || [];
    const lo = Math.min(startRow, currentRow);
    const hi = Math.max(startRow, currentRow);
    const nextNum = Number(value) || 0;
    const ids = [];
    let skippedContract = 0;

    for (let i = lo; i <= hi; i++) {
      if (i === startRow) continue;
      const row = rows[i];
      if (!row) continue;
      if (field === 'revised_selling_bud' && (row.contract_vs_extra || '').toLowerCase() === 'contract') {
        skippedContract += 1;
        continue;
      }
      const currentNum = Number(row[field]) || 0;
      if (currentNum === nextNum) continue;
      ids.push(row.id);
    }

    if (ids.length === 0) {
      if (skippedContract > 0) {
        setToast({ message: `Skipped ${skippedContract} contract row${skippedContract === 1 ? '' : 's'} (Revised Selling Bud is extra/revenue only)`, type: 'success' });
        setTimeout(() => setToast(null), 3000);
      }
      return;
    }

    setData(prev => prev.map(row => {
      if (!ids.includes(row.id)) return row;
      const patched = { ...row, [field]: nextNum };
      if (field === 'percent_complete') patched.etc_override = false;
      if (field === 'revised_selling_bud') patched.revised_selling_override = true;
      return patched;
    }));

    const now = Date.now();
    setRecentlyModified(prev => {
      const next = { ...prev };
      ids.forEach((id) => { next[`${id}-${field}`] = { field, timestamp: now }; });
      return next;
    });
    setTimeout(() => {
      setRecentlyModified(prev => {
        const next = { ...prev };
        ids.forEach((id) => { delete next[`${id}-${field}`]; });
        return next;
      });
    }, 5000);

    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/forecasting-dashboard/${jobNumber}/bulk-fill`,
        { field, value: nextNum, ids },
        syncHeaders()
      );
      if (response.data?.success && Array.isArray(response.data.data)) {
        const byId = new Map(response.data.data.map(r => [r.id, r]));
        setData(prev => prev.map(row => byId.get(row.id) || row));
      }
      const skipped = (response.data?.skipped || []).filter(s => s.reason === 'contract').length;
      const totalSkipped = skipped + skippedContract;
      if (totalSkipped > 0) {
        setToast({
          message: `Filled ${ids.length} row${ids.length === 1 ? '' : 's'}; skipped ${totalSkipped} contract row${totalSkipped === 1 ? '' : 's'}`,
          type: 'success',
        });
        setTimeout(() => setToast(null), 3000);
      }
    } catch (err) {
      alert('Failed to fill cells: ' + (err.message || err));
      if (fetchDataRef.current) fetchDataRef.current();
    }
  }, [instance, accounts, jobNumber, syncHeaders, setData]);

  useEffect(() => {
    if (!fillDrag.active) return;

    const getScrollElement = () => listScrollRef.current?.firstElementChild;

    const handleMouseMove = (e) => {
      const scrollEl = getScrollElement();
      const rows = filteredDataRef.current || [];
      const rowCount = Math.max(rows.length, 1);
      if (scrollEl) {
        const rect = scrollEl.getBoundingClientRect();
        const relativeY = e.clientY - rect.top + scrollEl.scrollTop;
        const rowIndex = Math.max(0, Math.min(Math.floor(relativeY / FD_ROW_HEIGHT), rowCount - 1));
        setFillDrag(prev => (prev.active && prev.currentRow !== rowIndex ? { ...prev, currentRow: rowIndex } : prev));

        if (fillAutoScrollRef.current) cancelAnimationFrame(fillAutoScrollRef.current);

        const distFromBottom = rect.bottom - e.clientY;
        const distFromTop = e.clientY - rect.top;
        const mouseY = e.clientY;

        if (distFromBottom < FD_FILL_AUTO_SCROLL_ZONE && distFromBottom > 0) {
          const speed = FD_FILL_AUTO_SCROLL_SPEED * (1 - distFromBottom / FD_FILL_AUTO_SCROLL_ZONE);
          const scrollDown = () => {
            if (!fillDragRef.current.active) return;
            const el = getScrollElement();
            if (el) {
              el.scrollTop += Math.max(1, speed);
              const newRelY = mouseY - rect.top + el.scrollTop;
              const newRow = Math.max(0, Math.min(Math.floor(newRelY / FD_ROW_HEIGHT), rowCount - 1));
              setFillDrag(prev => (prev.active ? { ...prev, currentRow: newRow } : prev));
            }
            fillAutoScrollRef.current = requestAnimationFrame(scrollDown);
          };
          fillAutoScrollRef.current = requestAnimationFrame(scrollDown);
        } else if (distFromTop < FD_FILL_AUTO_SCROLL_ZONE && distFromTop > 0) {
          const speed = FD_FILL_AUTO_SCROLL_SPEED * (1 - distFromTop / FD_FILL_AUTO_SCROLL_ZONE);
          const scrollUp = () => {
            if (!fillDragRef.current.active) return;
            const el = getScrollElement();
            if (el) {
              el.scrollTop -= Math.max(1, speed);
              const newRelY = mouseY - rect.top + el.scrollTop;
              const newRow = Math.max(0, Math.min(Math.floor(newRelY / FD_ROW_HEIGHT), rowCount - 1));
              setFillDrag(prev => (prev.active ? { ...prev, currentRow: newRow } : prev));
            }
            fillAutoScrollRef.current = requestAnimationFrame(scrollUp);
          };
          fillAutoScrollRef.current = requestAnimationFrame(scrollUp);
        }
      } else {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const rowEl = el?.closest?.('[data-fd-row-index]');
        if (rowEl) {
          const rowIndex = parseInt(rowEl.getAttribute('data-fd-row-index'), 10);
          if (Number.isInteger(rowIndex)) {
            setFillDrag(prev => (prev.active && prev.currentRow !== rowIndex ? { ...prev, currentRow: rowIndex } : prev));
          }
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', applyFillRange);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', applyFillRange);
      if (fillAutoScrollRef.current) cancelAnimationFrame(fillAutoScrollRef.current);
    };
  }, [fillDrag.active, applyFillRange]);

  // Precomputed values for the virtual table
  const fdVisibleCols = useMemo(
    () => applyColumnOrder(
      dashboardView === 'maestro' ? MAESTRO_COLUMNS : FORECASTING_COLUMNS,
      dashboardView === 'maestro' ? maestroColOrder.columnOrder : forecastingColOrder.columnOrder
    ).filter(c => currentVisibleColumns.has(c.key)),
    [dashboardView, currentVisibleColumns, maestroColOrder.columnOrder, forecastingColOrder.columnOrder]
  );
  const fdFrozenOffsets = useMemo(() => {
    const offsets = {};
    let left = 40; // after checkbox column
    fdVisibleCols.forEach(col => {
      if (col.frozen) offsets[col.key] = left;
      left += columnWidths[col.key] || col.width;
    });
    return offsets;
  }, [fdVisibleCols, columnWidths]);
  const fdTotalWidth = useMemo(
    () => 40 + fdVisibleCols.reduce((sum, c) => sum + (columnWidths[c.key] || c.width), 0),
    [fdVisibleCols, columnWidths]
  );

  // Sync header scroll with List's horizontal scroll using DOM ref.
  // The sync is bidirectional on purpose. It used to be one-way (body -> header), which
  // meant the only way to reach the right-hand columns was to scroll the row area. With
  // no rows loaded there is nothing to scroll, so this ~2800px-wide grid stranded most
  // of its columns off the edge of the card at every viewport width, phone or desktop.
  const syncingRef = useRef(false);
  useLayoutEffect(() => {
    const wrapper = listScrollRef.current;
    const header = fdHeaderRef.current;
    const scrollEl = wrapper ? wrapper.firstElementChild : null;

    // Mirror one scroller's position onto the others, ignoring the scroll events that
    // our own writes trigger so the two listeners cannot ping-pong.
    const mirror = (from) => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      const sl = from.scrollLeft;
      for (const el of [scrollEl, fdHeaderRef.current, fdSubtotalRef.current]) {
        if (el && el !== from) el.scrollLeft = sl;
      }
      // Release on the next frame; assigning scrollLeft queues a scroll event.
      requestAnimationFrame(() => { syncingRef.current = false; });
    };

    const onBody = scrollEl ? () => mirror(scrollEl) : null;
    const onHeader = header ? () => mirror(header) : null;
    if (scrollEl && onBody) scrollEl.addEventListener('scroll', onBody, { passive: true });
    if (header && onHeader) header.addEventListener('scroll', onHeader, { passive: true });
    return () => {
      if (scrollEl && onBody) scrollEl.removeEventListener('scroll', onBody);
      if (header && onHeader) header.removeEventListener('scroll', onHeader);
    };
  });

  // Analytics calculations (use computedData so FAC/profit match on-screen formulas)
  const analytics = useMemo(() => {
    if (computedData.length === 0) return null;
    
    const numericCols = COLUMNS.filter(c => c.type === 'number').map(c => c.key);
    const percentCols = COLUMNS.filter(c => c.type === 'percent').map(c => c.key);
    
    const totals = {};
    numericCols.forEach(key => { totals[key] = 0; });
    
    computedData.forEach(row => {
      numericCols.forEach(key => { totals[key] += Number(row[key]) || 0; });
    });
    
    const percentAvgs = {};
    percentCols.forEach(key => {
      percentAvgs[key] = computedData.reduce((sum, row) => sum + (Number(row[key]) || 0), 0) / computedData.length;
    });
    
    // Forecasted Margin = (Revised Selling Bud - FAC) / FAC
    const totalRevisedSellingBud = totals.revised_selling_bud || 0;
    const totalFAC = totals.fac_forecast_at_completion || 0;
    const forecastedMargin = totalFAC !== 0 ? (totalRevisedSellingBud / totalFAC) : 0;
    const forecastedMarginPercent = totalFAC !== 0 ? ((totalRevisedSellingBud - totalFAC) / totalFAC * 100) : 0;

    // Original Margin = Original Selling (from op code 90001) / Orig Bud
    const revenueRow = computedData.find(row => String(row.operation_code).trim() === '90001');
    const originalSelling = revenueRow ? (Number(revenueRow.revised_selling_bud) || 0) : 0;
    const totalOrigBud = totals.orig_bud || 0;
    const originalMarginPercent = totalOrigBud !== 0 ? ((originalSelling - totalOrigBud) / totalOrigBud * 100) : 0;

    // Contract value breakdowns
    const totalContractValue = totalRevisedSellingBud;
    const totalBaseContractValue = computedData
      .filter(r => (r.contract_vs_extra || '').toLowerCase() === 'contract')
      .reduce((sum, r) => sum + (Number(r.revised_selling_bud) || 0), 0);
    const totalExtraValue = computedData
      .filter(r => (r.contract_vs_extra || '').toLowerCase() === 'extra')
      .reduce((sum, r) => sum + (Number(r.revised_selling_bud) || 0), 0);

    // EVM Metrics
    // PV (Planned Value) = Sum of Orig Bud
    const PV = totals.orig_bud || 0;
    // AC (Actual Cost) = Sum of Actual
    const AC = totals.actual || 0;
    // EV (Earned Value) = Sum of (% Complete * Orig Bud per row)
    const EV = computedData.reduce((sum, row) => {
      const percentComplete = (Number(row.percent_complete) || 0) / 100; // Convert from % to decimal
      const origBud = Number(row.orig_bud) || 0;
      return sum + (percentComplete * origBud);
    }, 0);
    
    // CPI (Cost Performance Index) = EV / AC
    const CPI = AC !== 0 ? (EV / AC) : 0;
    // SPI (Schedule Performance Index) = EV / PV
    const SPI = PV !== 0 ? (EV / PV) : 0;
    // CV (Cost Variance) = EV - AC
    const CV = EV - AC;
    // SV (Schedule Variance) = EV - PV
    const SV = EV - PV;

    return {
      totals,
      percentAvgs,
      rowCount: computedData.length,
      budgetVariance: (totals.revised_bud || 0) - (totals.orig_bud || 0),
      budgetVariancePercent: totals.orig_bud ? (((totals.revised_bud || 0) - (totals.orig_bud || 0)) / totals.orig_bud * 100) : 0,
      forecastedMargin,
      forecastedMarginPercent,
      totalRevisedSellingBud,
      totalFAC,
      originalSelling,
      originalMarginPercent,
      // Contract value breakdowns
      totalContractValue, totalBaseContractValue, totalExtraValue,
      // EVM metrics
      PV, AC, EV, CPI, SPI, CV, SV,
    };
  }, [computedData, COLUMNS]);

  // Subtotal computation for footer rows
  const subtotalRows = useMemo(() => {
    if (subtotalMode.size === 0 || filteredData.length === 0) return [];
    const cols = dashboardView === 'maestro' ? MAESTRO_COLUMNS : FORECASTING_COLUMNS;
    const numericKeys = cols.filter(c => c.type === 'number').map(c => c.key);
    const percentKeys = cols.filter(c => c.type === 'percent').map(c => c.key);

    const buildTotals = (rows, label) => {
      const totals = { _subtotalLabel: label, _rowCount: rows.length };
      numericKeys.forEach(k => { totals[k] = rows.reduce((s, r) => s + (Number(r[k]) || 0), 0); });
      percentKeys.forEach(k => {
        const vals = rows.map(r => Number(r[k]) || 0);
        totals[k] = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      });
      return totals;
    };

    const result = [];

    if (subtotalMode.has('byDiscipline')) {
      const groups = {};
      filteredData.forEach(row => {
        const discipline = getDisciplineForCode(row.operation_code);
        if (!groups[discipline]) groups[discipline] = [];
        groups[discipline].push(row);
      });
      const sortOrder = { 'Contract Expenses': 0, 'Indirect Labour': 1, 'Direct Labour': 2, 'Revenue': 98, 'Other': 99 };
      Object.entries(groups)
        .sort(([a], [b]) => {
          const oa = sortOrder[a] ?? 50;
          const ob = sortOrder[b] ?? 50;
          if (oa !== ob) return oa - ob;
          return a.localeCompare(b);
        })
        .forEach(([discipline, rows]) => result.push(buildTotals(rows, `${discipline} (${rows.length})`)));
    }

    if (subtotalMode.has('overall')) {
      result.push(buildTotals(filteredData, 'TOTAL'));
    }

    return result;
  }, [subtotalMode, filteredData, dashboardView]);

  // Add new row
  const handleAddRow = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/${apiEndpoint}/${jobNumber}`, {}, syncHeaders());
      if (response.data.success) setData(prev => [...prev, response.data.data]);
    } catch (err) {
      alert('Failed to add row: ' + err.message);
    }
  };

  // Delete selected rows
  const handleDeleteSelected = async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Delete ${selectedRows.size} selected row(s)?`)) return;
    
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/${apiEndpoint}/${jobNumber}`, { ids: [...selectedRows] }, syncHeaders());
      if (response.data.success) {
        setData(prev => prev.filter(row => !selectedRows.has(row.id)));
        setSelectedRows(new Set());
      }
    } catch (err) {
      alert('Failed to delete rows: ' + err.message);
    }
  };

  // Update cell value - with recalculation for formula fields in forecasting dashboard
  // FORMULA RULES:
  // - ETC, %complete, actual, revised_bud, revised_selling_bud changes trigger auto-recalculation
  // - revised_selling_bud is blocked for 'contract' type rows (server enforces, UI shows warning)
  // - Modified cells get 5-second color highlight
  const handleCellUpdate = async (rowId, field, value) => {
    // Safety: never save values typed while a converted-currency view is on —
    // they would be misread as native-currency amounts.
    if (fdConvertedView) {
      clearEditingIfStill(rowId, field);
      return;
    }
    try {
      const colDef = FORECASTING_COLUMNS.find(c => c.key === field) || MAESTRO_COLUMNS.find(c => c.key === field);
      const isNumeric = colDef && (colDef.type === 'number' || colDef.type === 'percent');
      if (isNumeric && (value === '' || value === null || value === undefined)) {
        value = 0;
      }

      const currentRow = data.find(r => r.id === rowId);

      // Check if trying to edit revised_selling_bud on a contract row
      if (field === 'revised_selling_bud') {
        if (currentRow && (currentRow.contract_vs_extra || '').toLowerCase() === 'contract') {
          alert('Cannot override Revised Selling Budget for Contract type rows.\nOnly Extra type rows can be overridden.');
          clearEditingIfStill(rowId, field);
          return;
        }
      }

      // Click-and-blur on an unchanged 0 (blank editor) should not save or recalc
      if (isNumeric && currentRow) {
        const currentNum = Number(currentRow[field]) || 0;
        const nextNum = Number(value) || 0;
        if (currentNum === nextNum) {
          clearEditingIfStill(rowId, field);
          return;
        }
      }
      
      // First update the cell value
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/${apiEndpoint}/${jobNumber}/${rowId}`, { [field]: value }, syncHeaders());
      if (response.data.success) {
        // Track this cell as recently modified for 5-second highlight
        const modifiedKey = `${rowId}-${field}`;
        setRecentlyModified(prev => ({
          ...prev,
          [modifiedKey]: { field, timestamp: Date.now() }
        }));
        
        // Clear the highlight after 5 seconds
        setTimeout(() => {
          setRecentlyModified(prev => {
            const next = { ...prev };
            delete next[modifiedKey];
            return next;
          });
        }, 5000);
        
        // Send email notifications for @mentions in notes
        if (field === 'note' && value) {
          console.log('[Mention Debug] Checking note field for @mentions...');
          console.log('[Mention Debug] Note value:', value);
          console.log('[Mention Debug] Job Number:', jobNumber);
          
          // Extract @mentions (emails) from the note
          const mentionRegex = /@([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
          const mentions = [];
          let match;
          while ((match = mentionRegex.exec(value)) !== null) {
            mentions.push(match[1]); // Get the email without @
          }
          
          console.log('[Mention Debug] Extracted mentions:', mentions);
          
          // Send notification if there are mentions
          if (mentions.length > 0) {
            try {
              console.log('[Mention Debug] Sending notification API call...');
              console.log('[Mention Debug] Payload:', {
                mentions: [...new Set(mentions)],
                noteContent: value,
                projectId: null,
                projectName: `Job ${jobNumber}`,
                context: 'Forecasting Dashboard'
              });
              
              const mentionResponse = await authenticatedApiCall(instance, accounts, 'POST', '/notifications/mention', {
                mentions: [...new Set(mentions)],
                noteContent: value,
                projectId: null,
                projectName: `Job ${jobNumber}`,
                context: 'Forecasting Dashboard'
              }, syncHeaders());
              
              console.log('[Mention Debug] API Response:', mentionResponse);
              console.log(`[Mention] Sent notifications to: ${mentions.join(', ')}`);
              
              // Show toast notification to user
              if (mentionResponse.data?.success) {
                const sentCount = mentionResponse.data?.results?.filter(r => r.status === 'sent')?.length || mentions.length;
                const emailList = mentions.join(', ');
                setToast({ 
                  message: `Email notification sent to ${emailList}`, 
                  type: 'success' 
                });
                setTimeout(() => setToast(null), 3000);
              }
            } catch (mentionErr) {
              console.error('[Mention Debug] API call failed:', mentionErr);
              console.error('[Mention Debug] Error details:', mentionErr.response?.data || mentionErr.message);
              console.error('[Mention] Failed to send notifications:', mentionErr);
              // Don't block the main update if notification fails
            }
          } else {
            console.log('[Mention Debug] No mentions found in note');
          }
        }
        
        // For forecasting dashboard, recalculate formulas when certain fields change
        // Added etc_estimate_to_complete to triggers for new formula logic
        const recalcTriggers = ['percent_complete', 'actual', 'revised_bud', 'revised_selling_bud', 'etc_estimate_to_complete'];
        if (dashboardView === 'forecasting' && recalcTriggers.includes(field)) {
          // Trigger recalculation (auto-refresh)
          const recalcResponse = await authenticatedApiCall(instance, accounts, 'PUT', `/forecasting-dashboard/${jobNumber}/${rowId}/recalculate`, {}, syncHeaders());
          if (recalcResponse.data.success) {
            setData(prev => prev.map(row => row.id === rowId ? recalcResponse.data.data : row));
          } else {
            setData(prev => prev.map(row => row.id === rowId ? response.data.data : row));
          }
        } else {
          setData(prev => prev.map(row => row.id === rowId ? response.data.data : row));
        }
      }
    } catch (err) {
      // Check if the error is about contract override restriction
      if (err.message && err.message.includes('Contract type rows')) {
        alert(err.message);
      } else {
        alert('Failed to update: ' + err.message);
      }
    }
    clearEditingIfStill(rowId, field);
  };

  // Import a Maestro Reports batch into the Forecasting Dashboard.
  // Opens a confirmation modal with a batch picker (defaults to the latest batch).
  const handleImportData = async () => {
    setImportDataLoading(true);
    try {
      // Load the available Maestro Reports batches so the user can pick which
      // one to import (instead of silently taking the latest).
      const batchRes = await authenticatedApiCall(
        instance, accounts, 'GET', `/forecasting-dashboard/${jobNumber}/maestro-batches`
      );
      const batches = batchRes.data?.data || [];
      if (batches.length === 0) {
        setImportDataModal({ type: 'error', message: 'No Maestro Reports data found for this project. Please import data via the Maestro page first.' });
        return;
      }
      setImportBatches(batches);
      setImportSelectedBatchId(batches[0].id); // newest first — default to latest

      // Warn only when current unfinalized WORK would be lost: rows whose
      // % Complete / ETC differ from the frozen snapshot (or were never frozen).
      const num = (v) => parseFloat(v) || 0;
      const hasUnsnapshottedWork = forecastingData.some(r =>
        (num(r.percent_complete) !== 0 || num(r.etc_estimate_to_complete) !== 0) &&
        (num(r.percent_complete) !== num(r.percent_complete_prev) || num(r.etc_estimate_to_complete) !== num(r.etc_prev))
      );
      if (hasUnsnapshottedWork) {
        setImportDataModal({ type: 'confirm-unfinalized' });
      } else if (forecastingData.length > 0) {
        setImportDataModal({ type: 'confirm-replace' });
      } else {
        setImportDataModal({ type: 'confirm-fresh' });
      }
    } catch (err) {
      console.error('Import prep error:', err);
      setImportDataModal({ type: 'error', message: err.response?.data?.error || err.message });
    } finally {
      setImportDataLoading(false);
    }
  };

  // Batch picker shown in every Import Data confirmation modal.
  // Newest batch is preselected; the user can import any historical batch.
  const renderImportBatchPicker = () => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
        Maestro import to use ({importBatches.length} available)
      </label>
      <select
        value={importSelectedBatchId || ''}
        onChange={(e) => setImportSelectedBatchId(parseInt(e.target.value, 10))}
        style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#f9fafb', color: '#111827', cursor: 'pointer' }}
      >
        {importBatches.map((batch, idx) => (
          <option key={batch.id} value={batch.id}>
            {formatMaestroBatchOption(batch, { isLatest: idx === 0, includeImporter: true })}
          </option>
        ))}
      </select>
    </div>
  );

  const executeImportData = async () => {
    setImportDataModal(null);
    setImportDataLoading(true);
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/forecasting-dashboard/${jobNumber}/import-from-maestro-reports`,
        { clearExisting: true, batchId: importSelectedBatchId || undefined },
        syncHeaders()
      );
      if (response.data.success) {
        await fetchData();
        setDashboardView('forecasting');
        setImportDataModal({
          type: 'success',
          count: response.data.count,
          skipped: response.data.skippedTotalRows || 0,
          preservedPrev: response.data.preserved_prev || 0,
          batch: response.data.batch || null,
          fx: response.data.fx || null,
        });
      } else {
        setImportDataModal({ type: 'error', message: response.data.error || 'Unknown error' });
      }
    } catch (err) {
      console.error('Import error:', err);
      setImportDataModal({ type: 'error', message: err.response?.data?.error || err.message });
    } finally {
      setImportDataLoading(false);
    }
  };

  // Handle export — Excel (.xlsx), with a choice of filtered vs complete rows
  // and visible vs all columns. Numbers stay numeric so Excel can sum/filter.
  const handleExport = async ({ columns: columnsScope = 'visible', rows: rowsScope = 'filtered' } = {}) => {
    setShowExportDropdown(false);
    setExportLoading(true);
    try {
      const orderedCols = applyColumnOrder(COLUMNS, dashboardView === 'maestro' ? maestroColOrder.columnOrder : forecastingColOrder.columnOrder);
      const cols = columnsScope === 'visible'
        ? orderedCols.filter(c => currentVisibleColumns.has(c.key))
        : orderedCols;

      // Complete table = every row (still ordered by the active sort);
      // Filtered = exactly what is on screen (filters + search + sort).
      let exportRows = filteredData;
      if (rowsScope === 'all') {
        exportRows = sortConfig.key ? [...computedData].sort(makeSortComparator(sortConfig)) : computedData;
      }

      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'LGL Tools';
      wb.created = new Date();
      const ws = wb.addWorksheet(dashboardView === 'maestro' ? 'Maestro Data' : 'Forecast Data', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      ws.columns = cols.map(col => ({
        header: col.label,
        key: col.key,
        width: Math.max(10, Math.min(60, Math.round((columnWidths[col.key] || col.width || 100) / 7))),
      }));

      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
      headerRow.alignment = { vertical: 'middle' };
      headerRow.height = 20;

      for (const row of exportRows) {
        const excelRow = ws.addRow(cols.map(col => {
          const value = row[col.key];
          if (value === null || value === undefined || value === '') return null;
          if (col.type === 'number' || col.type === 'percent') {
            const n = parseFloat(value);
            return isNaN(n) ? null : n;
          }
          return String(value).trim();
        }));
        cols.forEach((col, i) => {
          if (col.type === 'number') excelRow.getCell(i + 1).numFmt = '#,##0.00';
          if (col.type === 'percent') excelRow.getCell(i + 1).numFmt = '0.0"%"';
        });
      }

      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } };

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const currencySuffix = fdConvertedView ? `_${fdCurrencyView}` : '';
      a.download = exportFilename({
        job: jobNumber,
        reportSlug: `${dashboardView === 'maestro' ? 'Maestro' : 'Forecasting'}_Dashboard${rowsScope === 'all' ? '_Complete' : ''}${currencySuffix}`,
        ext: 'xlsx',
      });
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
      alert('Excel export failed: ' + (err.message || err));
    } finally {
      setExportLoading(false);
    }
  };

  // Generate PDF report with logo. options: title, dateLabel, filename, columns, download (default true).
  const generatePdfReport = async (reportData, analyticsData = null, evmHistoryData = [], options = {}) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const reportTitle = options.title || 'Forecasting Dashboard - Finalization Report';
    const dateLabel = options.dateLabel || 'Finalized';
    const tableTitle = options.tableTitle || 'Forecasting Dashboard - Data Table';
    const pdfColumns = options.columns || FORECASTING_COLUMNS;
    const filename = options.filename || exportFilename({ job: jobNumber, reportSlug: 'Forecasting_Finalization', ext: 'pdf', date: now });
    const shouldDownload = options.download !== false;

    // Helper for currency formatting in analytics
    const fmtCurrency = (val) => {
      const num = parseFloat(val);
      if (isNaN(num)) return '$0.00';
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // ========== PAGE 1: ANALYTICS SUMMARY ==========
    const { y: letterheadY } = await applyLetterhead(doc, {
      instance, accounts, jobNumber, margin: 14, startY: 12,
    });

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(reportTitle, pageWidth / 2, letterheadY + 2, { align: 'center' });
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Job Number: ${jobNumber}`, pageWidth / 2, letterheadY + 8, { align: 'center' });
    doc.text(`${dateLabel}: ${dateStr} at ${timeStr}`, pageWidth / 2, letterheadY + 14, { align: 'center' });
    
    // Analytics Summary Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Project Summary', 14, letterheadY + 24);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    let yPos = letterheadY + 31;
    const colWidth = (pageWidth - 28) / 4;
    
    if (analyticsData) {
      // Row 1: Budget Overview
      const row1 = [
        { label: 'Total Rows', value: String(analyticsData.rowCount || reportData.length) },
        { label: 'Original Budget', value: fmtCurrency(analyticsData.totals?.orig_bud) },
        { label: 'Revised Budget', value: fmtCurrency(analyticsData.totals?.revised_bud) },
        { label: 'Actual', value: fmtCurrency(analyticsData.totals?.actual) },
      ];
      row1.forEach((m, i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(m.label + ':', 14 + (i * colWidth), yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value, 14 + (i * colWidth), yPos + 5);
      });
      
      yPos += 14;
      // Row 2: ETC and FAC
      const row2 = [
        { label: 'ETC (Est. Balance)', value: fmtCurrency(analyticsData.totals?.etc_estimate_to_complete) },
        { label: 'ETC Prev', value: fmtCurrency(analyticsData.totals?.etc_prev) },
        { label: 'Total FAC', value: fmtCurrency(analyticsData.totalFAC) },
        { label: 'FAC Prev', value: fmtCurrency(analyticsData.totals?.fac_prev) },
      ];
      row2.forEach((m, i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(m.label + ':', 14 + (i * colWidth), yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value, 14 + (i * colWidth), yPos + 5);
      });
      
      yPos += 14;
      // Row 3: Costs
      const row3 = [
        { label: 'Comm Costs', value: fmtCurrency(analyticsData.totals?.comm_costs) },
        { label: 'Budget Balance', value: fmtCurrency(analyticsData.totals?.bud_bal) },
        { label: 'Revised Selling Bud', value: fmtCurrency(analyticsData.totalRevisedSellingBud) },
        { label: 'Revised Selling Bud Prev', value: fmtCurrency(analyticsData.totals?.revised_selling_bud_prev) },
      ];
      row3.forEach((m, i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(m.label + ':', 14 + (i * colWidth), yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value, 14 + (i * colWidth), yPos + 5);
      });
      
      yPos += 14;
      const row3b = [
        { label: 'Original Selling', value: fmtCurrency(analyticsData.originalSelling) },
      ];
      row3b.forEach((m, i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(m.label + ':', 14 + (i * colWidth), yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value, 14 + (i * colWidth), yPos + 5);
      });
      
      yPos += 14;
      // Row 3c: Contract Value Breakdown
      const row3c = [
        { label: 'Total Contract Value', value: fmtCurrency(analyticsData.totalContractValue) },
        { label: 'Base Contract Value', value: fmtCurrency(analyticsData.totalBaseContractValue) },
        { label: 'Extra Value', value: fmtCurrency(analyticsData.totalExtraValue) },
      ];
      row3c.forEach((m, i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(m.label + ':', 14 + (i * colWidth), yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value, 14 + (i * colWidth), yPos + 5);
      });

      yPos += 14;
      // Row 4: Profit and Margins
      const row4 = [
        { label: 'Forecasted Profit', value: fmtCurrency(analyticsData.totals?.estimated_profit_at_completion) },
        { label: 'Forecasted Margin', value: (analyticsData.forecastedMarginPercent || 0).toFixed(1) + '%' },
        { label: 'Original Margin', value: (analyticsData.originalMarginPercent || 0).toFixed(1) + '%' },
        { label: 'Budget Variance', value: fmtCurrency(analyticsData.budgetVariance) },
      ];
      row4.forEach((m, i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(m.label + ':', 14 + (i * colWidth), yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value, 14 + (i * colWidth), yPos + 5);
      });
      
      // EVM Metrics Section
      yPos += 18;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Earned Value Management (EVM) Metrics', 14, yPos);
      
      yPos += 8;
      doc.setFontSize(10);
      // EVM Base Values
      const evmBase = [
        { label: 'PV (Planned Value)', value: fmtCurrency(analyticsData.PV) },
        { label: 'AC (Actual Cost)', value: fmtCurrency(analyticsData.AC) },
        { label: 'EV (Earned Value)', value: fmtCurrency(analyticsData.EV) },
        { label: '', value: '' },
      ];
      evmBase.forEach((m, i) => {
        if (m.label) {
          doc.setFont('helvetica', 'bold');
          doc.text(m.label + ':', 14 + (i * colWidth), yPos);
          doc.setFont('helvetica', 'normal');
          doc.text(m.value, 14 + (i * colWidth), yPos + 5);
        }
      });
      
      yPos += 14;
      // EVM Performance Indices
      const evmIndices = [
        { label: 'CPI (Cost Performance)', value: (analyticsData.CPI || 0).toFixed(3), good: (analyticsData.CPI || 0) >= 1 },
        { label: 'SPI (Schedule Performance)', value: (analyticsData.SPI || 0).toFixed(3), good: (analyticsData.SPI || 0) >= 1 },
        { label: 'CV (Cost Variance)', value: fmtCurrency(analyticsData.CV), good: (analyticsData.CV || 0) >= 0 },
        { label: 'SV (Schedule Variance)', value: fmtCurrency(analyticsData.SV), good: (analyticsData.SV || 0) >= 0 },
      ];
      evmIndices.forEach((m, i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(m.label + ':', 14 + (i * colWidth), yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value + (m.good ? ' (Good)' : ' (!)'), 14 + (i * colWidth), yPos + 5);
      });
      
      // Percentage Averages
      yPos += 18;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Percentage Averages', 14, yPos);
      
      yPos += 8;
      doc.setFontSize(10);
      const percents = [
        { label: '% Complete', value: (analyticsData.percentAvgs?.percent_complete || 0).toFixed(1) + '%' },
        { label: '% Complete Prev', value: (analyticsData.percentAvgs?.percent_complete_prev || 0).toFixed(1) + '%' },
        { label: '% Margin', value: (analyticsData.percentAvgs?.percent_margin || 0).toFixed(1) + '%' },
        { label: 'Fore Var %', value: (analyticsData.percentAvgs?.fore_var_percent || 0).toFixed(1) + '%' },
      ];
      percents.forEach((m, i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(m.label + ':', 14 + (i * colWidth), yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(m.value, 14 + (i * colWidth), yPos + 5);
      });
      
      // ========== PAGE 2: VISUAL ANALYTICS ==========
      // Add a new page for graphical charts to ensure clean layout
      doc.addPage();
      
      // Add logo to analytics page too
      try {
        const img2 = new Image();
        img2.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img2.onload = resolve;
          img2.onerror = reject;
          img2.src = '/lgl-logo-cropped.png';
        });
        doc.addImage(img2, 'PNG', 14, 4, 14, 14);
      } catch (e) {
        console.log('Logo not added to analytics page:', e);
      }
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Visual Analytics', pageWidth / 2, 18, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Job Number: ${jobNumber}`, pageWidth / 2, 26, { align: 'center' });
      
      yPos = 35;
      
      // Budget Comparison Bar Chart - Larger Size
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Budget Comparison (PV, AC, EV)', 14, yPos);
      yPos += 6;
      
      const barWidth = 110;
      const barHeight = 14;
      const maxBudget = Math.max(
        analyticsData.PV || 0,
        analyticsData.AC || 0,
        analyticsData.EV || 0
      ) || 1;
      
      // Draw PV, AC, EV horizontal bars
      const bars = [
        { label: 'PV (Planned Value)', value: analyticsData.PV || 0, color: [3, 105, 161] },
        { label: 'AC (Actual Cost)', value: analyticsData.AC || 0, color: [124, 58, 237] },
        { label: 'EV (Earned Value)', value: analyticsData.EV || 0, color: [5, 150, 105] },
      ];
      
      bars.forEach((bar, idx) => {
        const barPosY = yPos + (idx * 18);
        const fillWidth = (bar.value / maxBudget) * barWidth;
        
        // Label
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(bar.label, 14, barPosY + 4);
        
        // Bar background
        doc.setFillColor(240, 240, 240);
        doc.rect(65, barPosY - 3, barWidth, barHeight, 'F');
        
        // Bar fill
        doc.setFillColor(bar.color[0], bar.color[1], bar.color[2]);
        doc.rect(65, barPosY - 3, fillWidth, barHeight, 'F');
        
        // Value
        doc.setFontSize(8);
        doc.text(fmtCurrency(bar.value), 180, barPosY + 4);
      });
      
      yPos += 60;
      
      // Performance Indices Section
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Performance Indices', 14, yPos);
      yPos += 8;
      
      const cpi = analyticsData.CPI || 0;
      const spi = analyticsData.SPI || 0;
      const cv = analyticsData.CV || 0;
      const sv = analyticsData.SV || 0;
      
      // CPI and SPI side by side with larger bars
      const gaugeWidth = 80;
      
      // CPI Bar
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('CPI (Cost Performance):', 14, yPos + 4);
      doc.setFillColor(240, 240, 240);
      doc.rect(70, yPos, gaugeWidth, 10, 'F');
      const cpiWidth = Math.min(cpi / 2, 1) * gaugeWidth;
      doc.setFillColor(cpi >= 1 ? 5 : 220, cpi >= 1 ? 150 : 38, cpi >= 1 ? 105 : 38);
      doc.rect(70, yPos, cpiWidth, 10, 'F');
      doc.setDrawColor(50, 50, 50);
      doc.setLineWidth(0.4);
      doc.line(70 + gaugeWidth/2, yPos - 1, 70 + gaugeWidth/2, yPos + 11);
      doc.setLineWidth(0.2);
      doc.text(cpi.toFixed(2) + (cpi >= 1 ? ' (Good)' : ' (!)'), 155, yPos + 6);
      
      yPos += 14;
      
      // SPI Bar
      doc.text('SPI (Schedule Performance):', 14, yPos + 4);
      doc.setFillColor(240, 240, 240);
      doc.rect(70, yPos, gaugeWidth, 10, 'F');
      const spiWidth = Math.min(spi / 2, 1) * gaugeWidth;
      doc.setFillColor(spi >= 1 ? 5 : 220, spi >= 1 ? 150 : 38, spi >= 1 ? 105 : 38);
      doc.rect(70, yPos, spiWidth, 10, 'F');
      doc.line(70 + gaugeWidth/2, yPos - 1, 70 + gaugeWidth/2, yPos + 11);
      doc.text(spi.toFixed(2) + (spi >= 1 ? ' (Good)' : ' (!)'), 155, yPos + 6);
      
      yPos += 16;
      
      // CV and SV as text with visual indicators
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Variance Analysis', 14, yPos);
      yPos += 7;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      
      // CV
      const cvColor = cv >= 0 ? [5, 150, 105] : [220, 38, 38];
      doc.setFillColor(cvColor[0], cvColor[1], cvColor[2]);
      doc.circle(18, yPos + 2, 2.5, 'F');
      doc.text(`Cost Variance (CV): ${fmtCurrency(cv)} ${cv >= 0 ? '(Under budget)' : '(Over budget)'}`, 24, yPos + 4);
      
      yPos += 10;
      
      // SV
      const svColor = sv >= 0 ? [5, 150, 105] : [220, 38, 38];
      doc.setFillColor(svColor[0], svColor[1], svColor[2]);
      doc.circle(18, yPos + 2, 2.5, 'F');
      doc.text(`Schedule Variance (SV): ${fmtCurrency(sv)} ${sv >= 0 ? '(Ahead)' : '(Behind)'}`, 24, yPos + 4);
      
      yPos += 12;
      
      // Legend
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text('Target: CPI/SPI = 1.0 (vertical line). Green = good performance, Red = needs attention.', 14, yPos);
      doc.setTextColor(0, 0, 0);
      
      // ========== EVM S-CURVE PAGE ==========
      if (evmHistoryData && evmHistoryData.length > 0) {
        doc.addPage();
        
        // Add logo
        try {
          const img3 = new Image();
          img3.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img3.onload = resolve;
            img3.onerror = reject;
            img3.src = '/lgl-logo-cropped.png';
          });
          doc.addImage(img3, 'PNG', 14, 4, 14, 14);
        } catch (e) {
          console.log('Logo not added to EVM page:', e);
        }
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('EVM S-Curve Analysis', pageWidth / 2, 18, { align: 'center' });
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Job Number: ${jobNumber} | ${evmHistoryData.length} periods of data`, pageWidth / 2, 26, { align: 'center' });
        
        // Chart dimensions
        const chartLeft = 30;
        const chartTop = 40;
        const chartWidth = pageWidth - 60;
        const chartHeight = 100;
        
        // Find max values for scaling
        const maxValue = Math.max(
          ...evmHistoryData.map(d => Math.max(d.planned_value || 0, d.actual_cost || 0, d.earned_value || 0))
        ) || 1;
        
        // Draw chart axes
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        // Y-axis
        doc.line(chartLeft, chartTop, chartLeft, chartTop + chartHeight);
        // X-axis
        doc.line(chartLeft, chartTop + chartHeight, chartLeft + chartWidth, chartTop + chartHeight);
        
        // Draw grid lines
        doc.setDrawColor(230, 230, 230);
        for (let i = 1; i <= 4; i++) {
          const y = chartTop + (chartHeight * i / 5);
          doc.line(chartLeft, y, chartLeft + chartWidth, y);
        }
        
        // Y-axis labels
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        for (let i = 0; i <= 5; i++) {
          const val = maxValue * (5 - i) / 5;
          const y = chartTop + (chartHeight * i / 5);
          doc.text(fmtCurrency(val), chartLeft - 2, y + 1, { align: 'right' });
        }
        
        // X-axis labels (period months)
        const pointSpacing = chartWidth / Math.max(evmHistoryData.length - 1, 1);
        evmHistoryData.forEach((d, idx) => {
          if (idx % Math.ceil(evmHistoryData.length / 10) === 0 || idx === evmHistoryData.length - 1) {
            const x = chartLeft + (idx * pointSpacing);
            doc.text(`M${d.period_month}`, x, chartTop + chartHeight + 6, { align: 'center' });
          }
        });
        
        // Draw lines function
        const drawLine = (dataKey, color, label) => {
          doc.setDrawColor(color[0], color[1], color[2]);
          doc.setLineWidth(0.8);
          
          let prevX = null, prevY = null;
          evmHistoryData.forEach((d, idx) => {
            const x = chartLeft + (idx * pointSpacing);
            const value = d[dataKey] || 0;
            const y = chartTop + chartHeight - (value / maxValue * chartHeight);
            
            if (prevX !== null && prevY !== null) {
              doc.line(prevX, prevY, x, y);
            }
            prevX = x;
            prevY = y;
          });
        };
        
        // Draw PV line (blue)
        drawLine('planned_value', [3, 105, 161], 'PV');
        
        // Draw AC line (purple)
        drawLine('actual_cost', [124, 58, 237], 'AC');
        
        // Draw EV line (green)
        drawLine('earned_value', [5, 150, 105], 'EV');
        
        // Legend
        let legendY = chartTop + chartHeight + 15;
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        
        // PV Legend
        doc.setFillColor(3, 105, 161);
        doc.rect(chartLeft, legendY, 10, 3, 'F');
        doc.text('PV (Planned Value) - Budget distribution over time', chartLeft + 14, legendY + 2.5);
        
        // AC Legend
        doc.setFillColor(124, 58, 237);
        doc.rect(chartLeft + 100, legendY, 10, 3, 'F');
        doc.text('AC (Actual Cost) - Cumulative spending', chartLeft + 114, legendY + 2.5);
        
        // EV Legend
        legendY += 6;
        doc.setFillColor(5, 150, 105);
        doc.rect(chartLeft, legendY, 10, 3, 'F');
        doc.text('EV (Earned Value) - Work completed value', chartLeft + 14, legendY + 2.5);
        
        // EVM Data Table below chart
        legendY += 15;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('EVM Period Data', 14, legendY);
        legendY += 5;
        
        // Create table of EVM data
        const evmTableColumns = [
          { header: 'Period', dataKey: 'period_month' },
          { header: 'PV', dataKey: 'planned_value' },
          { header: 'AC', dataKey: 'actual_cost' },
          { header: 'EV', dataKey: 'earned_value' },
          { header: 'CPI', dataKey: 'cpi' },
          { header: 'SPI', dataKey: 'spi' },
          { header: 'CV', dataKey: 'cv' },
          { header: 'SV', dataKey: 'sv' },
        ];
        
        const evmTableRows = evmHistoryData.map(d => ({
          period_month: `Month ${d.period_month}`,
          planned_value: fmtCurrency(d.planned_value),
          actual_cost: fmtCurrency(d.actual_cost),
          earned_value: fmtCurrency(d.earned_value),
          cpi: (parseFloat(d.cpi) || 0).toFixed(2),
          spi: (parseFloat(d.spi) || 0).toFixed(2),
          cv: fmtCurrency(d.cv),
          sv: fmtCurrency(d.sv),
        }));
        
        autoTable(doc, {
          startY: legendY,
          head: [evmTableColumns.map(c => c.header)],
          body: evmTableRows.map(row => evmTableColumns.map(c => row[c.dataKey])),
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 1.5 },
          headStyles: { fillColor: [52, 58, 64], textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 20 },
            1: { cellWidth: 28 },
            2: { cellWidth: 28 },
            3: { cellWidth: 28 },
            4: { cellWidth: 15 },
            5: { cellWidth: 15 },
            6: { cellWidth: 28 },
            7: { cellWidth: 28 },
          },
          margin: { left: 14, right: 14 },
        });
      }
      
    } else {
      doc.text(`Total Rows: ${reportData.length}`, 14, yPos);
    }
    
    // ========== PAGE 3+: DATA TABLE ==========
    doc.addPage();
    
    // Table page header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(tableTitle, 14, 15);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Job: ${jobNumber} | Rows: ${reportData.length} | Generated: ${dateStr}`, 14, 22);

    const tableColumns = pdfColumns.map(col => ({
      header: col.label,
      dataKey: col.key
    }));

    // Helper to format currency values (handles both strings and numbers)
    const formatCurrency = (val) => {
      const num = parseFloat(val);
      if (isNaN(num)) return '';
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    
    // Helper to format percent values (values are already stored as percentages, e.g., 50 = 50%)
    const formatPercent = (val) => {
      const num = parseFloat(val);
      if (isNaN(num)) return '';
      return num.toFixed(1) + '%';
    };

    // Format data for table - dynamically based on column types
    const tableData = reportData.map(row => {
      const formattedRow = {};
      pdfColumns.forEach(col => {
        const val = row[col.key];
        if (col.type === 'number') {
          formattedRow[col.key] = formatCurrency(val);
        } else if (col.type === 'percent') {
          formattedRow[col.key] = formatPercent(val);
        } else {
          // Text columns - truncate activity for PDF
          formattedRow[col.key] = col.key === 'activity' ? (val || '').substring(0, 35) : (val || '');
        }
      });
      return formattedRow;
    });

    // Generate table
    autoTable(doc, {
      columns: tableColumns,
      body: tableData,
      startY: 28,
      theme: 'grid',
      styles: { fontSize: 5, cellPadding: 1 },
      headStyles: { fillColor: [90, 154, 104], textColor: 255, fontStyle: 'bold', fontSize: 5 },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: pdfColumns.reduce((styles, col) => {
        if (col.type === 'number' || col.type === 'percent') {
          styles[col.key] = { cellWidth: 'auto', halign: 'right' };
        } else if (col.key === 'activity') {
          styles[col.key] = { cellWidth: 40 };
        } else {
          styles[col.key] = { cellWidth: 'auto' };
        }
        return styles;
      }, {}),
      margin: { left: 10, right: 10 },
      tableWidth: 'auto',
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 25, doc.internal.pageSize.getHeight() - 10);
      doc.text('LGL Tools - Forecasting Dashboard', 14, doc.internal.pageSize.getHeight() - 10);
    }

    if (shouldDownload) {
      doc.save(filename);
    }
    return doc;
  };

  const analyticsReportRows = () => (filteredData.length > 0 ? filteredData : data);

  const analyticsPdfOptions = (download) => ({
    title: `${dashboardView === 'maestro' ? 'Maestro' : 'Forecasting'} Analytics Report`,
    dateLabel: 'Generated',
    tableTitle: `${dashboardView === 'maestro' ? 'Maestro' : 'Forecasting'} Dashboard - Data Table`,
    filename: exportFilename({ job: jobNumber, reportSlug: 'Forecasting_Analytics', ext: 'pdf' }),
    columns: COLUMNS,
    download,
  });

  const handleAnalyticsDownloadPdf = async () => {
    if (!analytics) return;
    setAnalyticsExporting('pdf');
    try {
      await generatePdfReport(analyticsReportRows(), analytics, evmHistory, analyticsPdfOptions(true));
    } catch (err) {
      console.error('Analytics PDF export failed:', err);
      alert('PDF export failed: ' + (err.message || err));
    } finally {
      setAnalyticsExporting(null);
    }
  };

  const handleAnalyticsDownloadExcel = async () => {
    if (!analytics) return;
    setAnalyticsExporting('excel');
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'LGL Tools';
      wb.created = new Date();
      const viewLabel = dashboardView === 'maestro' ? 'Maestro' : 'Forecasting';
      const CURRENCY = '$#,##0.00';
      const PCT = '0.0"%"';
      const HEADER = 'FF0369A1';

      const styleHeader = (row) => {
        row.eachCell((cell) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER } };
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
          cell.alignment = { vertical: 'middle' };
        });
      };

      const addKvSheet = (name, title, pairs) => {
        const ws = wb.addWorksheet(name);
        ws.mergeCells(1, 1, 1, 2);
        const t = ws.getCell(1, 1);
        t.value = title;
        t.font = { bold: true, size: 14, color: { argb: HEADER } };
        ws.getCell(2, 1).value = `Job ${jobNumber}`;
        ws.getCell(2, 1).font = { size: 10, color: { argb: 'FF475569' } };
        ws.getCell(3, 1).value = `Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
        ws.getCell(3, 1).font = { size: 9, italic: true, color: { argb: 'FF94A3B8' } };
        const headerRow = ws.getRow(5);
        headerRow.getCell(1).value = 'Metric';
        headerRow.getCell(2).value = 'Value';
        styleHeader(headerRow);
        pairs.forEach((pair, i) => {
          const row = ws.getRow(6 + i);
          row.getCell(1).value = pair[0];
          row.getCell(2).value = pair[1];
          if (pair[2] === 'currency' && typeof pair[1] === 'number') row.getCell(2).numFmt = CURRENCY;
          if (pair[2] === 'percent' && typeof pair[1] === 'number') row.getCell(2).numFmt = PCT;
        });
        ws.getColumn(1).width = 36;
        ws.getColumn(2).width = 22;
        return ws;
      };

      addKvSheet('Summary', `${viewLabel} Analytics`, [
        ['Total Rows', analytics.rowCount],
        ['Original Budget', analytics.totals?.orig_bud || 0, 'currency'],
        ['Revised Budget', analytics.totals?.revised_bud || 0, 'currency'],
        ['Actual', analytics.totals?.actual || 0, 'currency'],
        ['Committed Costs', analytics.totals?.comm_costs || 0, 'currency'],
        ['Budget Balance', analytics.totals?.bud_bal || 0, 'currency'],
        ['Budget Variance', analytics.budgetVariance || 0, 'currency'],
        ['Budget Variance %', analytics.budgetVariancePercent || 0, 'percent'],
        ['ETC (Est. Balance)', analytics.totals?.etc_estimate_to_complete || 0, 'currency'],
        ['ETC Prev', analytics.totals?.etc_prev || 0, 'currency'],
        ['FAC (Est. Cost)', analytics.totals?.fac_forecast_at_completion || 0, 'currency'],
        ['FAC Prev', analytics.totals?.fac_prev || 0, 'currency'],
        ['Revised Selling Bud', analytics.totals?.revised_selling_bud || 0, 'currency'],
        ['Revised Selling Bud Prev', analytics.totals?.revised_selling_bud_prev || 0, 'currency'],
        ['Forecasted Profit', analytics.totals?.estimated_profit_at_completion || 0, 'currency'],
        ['Forecasted Margin %', analytics.forecastedMarginPercent || 0, 'percent'],
        ['Original Selling', analytics.originalSelling || 0, 'currency'],
        ['Original Margin %', analytics.originalMarginPercent || 0, 'percent'],
        ['Total Contract Value', analytics.totalContractValue || 0, 'currency'],
        ['Base Contract Value', analytics.totalBaseContractValue || 0, 'currency'],
        ['Extra Value', analytics.totalExtraValue || 0, 'currency'],
        ['PV (Planned Value)', analytics.PV || 0, 'currency'],
        ['AC (Actual Cost)', analytics.AC || 0, 'currency'],
        ['EV (Earned Value)', analytics.EV || 0, 'currency'],
        ['CPI', analytics.CPI || 0],
        ['SPI', analytics.SPI || 0],
        ['CV (Cost Variance)', analytics.CV || 0, 'currency'],
        ['SV (Schedule Variance)', analytics.SV || 0, 'currency'],
        ['% Complete (avg)', analytics.percentAvgs?.percent_complete || 0, 'percent'],
        ['% Complete Prev (avg)', analytics.percentAvgs?.percent_complete_prev || 0, 'percent'],
        ['% Margin (avg)', analytics.percentAvgs?.percent_margin || 0, 'percent'],
        ['Fore Var % (avg)', analytics.percentAvgs?.fore_var_percent || 0, 'percent'],
      ]);

      if (evmHistory && evmHistory.length > 0) {
        const evmWs = wb.addWorksheet('EVM History');
        evmWs.mergeCells(1, 1, 1, 8);
        evmWs.getCell(1, 1).value = `${viewLabel} EVM Period Data — Job ${jobNumber}`;
        evmWs.getCell(1, 1).font = { bold: true, size: 14, color: { argb: HEADER } };
        const evmHeaders = ['Period', 'PV', 'AC', 'EV', 'CPI', 'SPI', 'CV', 'SV'];
        const evmHeaderRow = evmWs.getRow(3);
        evmHeaders.forEach((h, i) => { evmHeaderRow.getCell(i + 1).value = h; });
        styleHeader(evmHeaderRow);
        evmHistory.forEach((d, i) => {
          const row = evmWs.getRow(4 + i);
          row.getCell(1).value = `Month ${d.period_month}`;
          row.getCell(2).value = Number(d.planned_value) || 0;
          row.getCell(3).value = Number(d.actual_cost) || 0;
          row.getCell(4).value = Number(d.earned_value) || 0;
          row.getCell(5).value = Number(d.cpi) || 0;
          row.getCell(6).value = Number(d.spi) || 0;
          row.getCell(7).value = Number(d.cv) || 0;
          row.getCell(8).value = Number(d.sv) || 0;
          [2, 3, 4, 7, 8].forEach((c) => { row.getCell(c).numFmt = CURRENCY; });
        });
        [18, 16, 16, 16, 10, 10, 16, 16].forEach((w, i) => { evmWs.getColumn(i + 1).width = w; });
      }

      const dataWs = wb.addWorksheet('Data');
      dataWs.mergeCells(1, 1, 1, COLUMNS.length);
      dataWs.getCell(1, 1).value = `${viewLabel} Data — Job ${jobNumber}`;
      dataWs.getCell(1, 1).font = { bold: true, size: 14, color: { argb: HEADER } };
      const dataHeader = dataWs.getRow(3);
      COLUMNS.forEach((col, i) => { dataHeader.getCell(i + 1).value = col.label; });
      styleHeader(dataHeader);
      analyticsReportRows().forEach((row, rIdx) => {
        const excelRow = dataWs.getRow(4 + rIdx);
        COLUMNS.forEach((col, i) => {
          const cell = excelRow.getCell(i + 1);
          const raw = row[col.key];
          if (col.type === 'number' || col.type === 'percent') {
            const n = parseFloat(raw);
            cell.value = Number.isFinite(n) ? n : 0;
            cell.numFmt = col.type === 'percent' ? PCT : CURRENCY;
          } else {
            cell.value = raw == null ? '' : String(raw);
          }
        });
      });
      COLUMNS.forEach((col, i) => { dataWs.getColumn(i + 1).width = Math.min(Math.max((col.label || '').length + 4, 12), 28); });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFilename({ job: jobNumber, reportSlug: 'Forecasting_Analytics', ext: 'xlsx' });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Analytics Excel export failed:', err);
      alert('Excel export failed: ' + (err.message || err));
    } finally {
      setAnalyticsExporting(null);
    }
  };

  const handleAnalyticsSendEmail = async () => {
    if (!analyticsEmailAddresses.trim() || !analytics) return;
    const recipients = analyticsEmailAddresses.split(/[,;\s]+/).filter((e) => e.includes('@'));
    if (recipients.length === 0) {
      setAnalyticsEmailResult({ type: 'error', message: 'No valid email addresses' });
      return;
    }
    setAnalyticsEmailSending(true);
    setAnalyticsEmailResult(null);
    try {
      const doc = await generatePdfReport(analyticsReportRows(), analytics, evmHistory, analyticsPdfOptions(false));
      const filename = `Forecasting_Analytics_${jobNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
      const viewLabel = dashboardView === 'maestro' ? 'Maestro' : 'Forecasting';
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/email/send', {
        to: recipients,
        subject: `${viewLabel} Analytics Report — Job ${jobNumber}`,
        htmlBody: `<p>Please find attached the ${viewLabel} analytics report for job ${jobNumber}.</p><p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.</p>`,
        attachments: [{ filename, fileblob: doc.output('datauristring').split(',')[1] }],
        feature: 'analytics_report',
      });
      if (response.data.success) {
        setAnalyticsEmailResult({ type: 'success', message: `Sent to ${recipients.length} recipient(s)` });
        setTimeout(() => { setShowAnalyticsEmailModal(false); setAnalyticsEmailResult(null); setAnalyticsEmailAddresses(''); }, 2000);
      } else {
        setAnalyticsEmailResult({ type: 'error', message: response.data.error || 'Failed to send' });
      }
    } catch (error) {
      setAnalyticsEmailResult({ type: 'error', message: error.response?.data?.error || error.message || 'Failed to send email' });
    } finally {
      setAnalyticsEmailSending(false);
    }
  };

  // Open finalize modal and fetch history
  const handleFinalizeClick = async () => {
    if (dashboardView !== 'forecasting') return;
    
    try {
      // Fetch finalization history and EVM history in parallel
      const [historyRes, evmRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/forecasting-dashboard/${jobNumber}/finalization-history`),
        authenticatedApiCall(instance, accounts, 'GET', `/forecasting-dashboard/${jobNumber}/evm-history`)
      ]);
      
      if (historyRes.data.success) {
        setFinalizationHistory(historyRes.data.data || []);
      }
      if (evmRes.data.success) {
        setEvmHistory(evmRes.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching finalization/EVM history:', err);
      setFinalizationHistory([]);
    }
    
    setFinalizeStep(1);
    setFinalizeModalOpen(true);
  };

  // Handle actual finalization with options
  const handleFinalize = async (copyValues, generatePdf) => {
    try {
      setLoading(true);
      setFinalizeModalOpen(false);
      
      // Generate PDF if requested (captures current state before copy)
      if (generatePdf) {
        await generatePdfReport(filteredData.length > 0 ? filteredData : data, analytics, evmHistory);
      }
      
      // Call finalize API with options
      const response = await authenticatedApiCall(
        instance, accounts, 'POST', 
        `/forecasting-dashboard/${jobNumber}/finalize`,
        { copyValues, generatePdf },
        syncHeaders()
      );
      
      if (response.data.success) {
        if (copyValues) {
          await fetchData();
        }
        let message = '';
        if (copyValues) {
          message += `Successfully copied values to previous period for ${response.data.count || 0} rows.`;
        }
        if (generatePdf) {
          message += (message ? '\n\n' : '') + 'PDF report has been downloaded.';
        }
        if (!copyValues && !generatePdf) {
          message = 'No action was taken.';
        }
        alert(message);
      } else {
        alert('Finalization failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error finalizing:', err);
      alert('Failed to finalize: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Column resize handlers
  const handleResizeStart = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { colKey, startX: e.clientX, startWidth: columnWidths[colKey] };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };
  
  const handleResizeMove = useCallback((e) => {
    if (!resizingRef.current) return;
    const { colKey, startX, startWidth } = resizingRef.current;
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff); // min width 50px
    setColumnWidths(prev => ({ ...prev, [colKey]: newWidth }));
  }, []);
  
  const handleResizeEnd = useCallback(() => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove]);

  // Format text with @mentions highlighted
  const formatMentions = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Regex to match @email patterns
    const mentionRegex = /(@[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,})/g;
    const parts = text.split(mentionRegex);
    
    if (parts.length === 1) return text; // No mentions found
    
    return parts.map((part, index) => {
      if (part.match(mentionRegex)) {
        // This is a mention - style it
        return (
          <span 
            key={index} 
            style={{ 
              color: '#0066cc', 
              fontWeight: '600',
              backgroundColor: '#e7f3ff',
              padding: '0 3px',
              borderRadius: '3px'
            }}
            title={`Mentioned: ${part.slice(1)}`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Format number for display
  const formatValue = (value, type, colKey) => {
    if (value === null || value === undefined || value === '') return '—';
    if (type === 'number') return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (type === 'percent') return `${Number(value).toFixed(1)}%`;
    // Format notes with @mention highlighting
    if (colKey === 'note') return formatMentions(value);
    return value;
  };

  const buttonStyle = {
    padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
    backgroundColor: 'white', color: '#374151', fontSize: '13px', fontWeight: '500',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s ease',
  };

  // Table displays all filtered data with scroll for rows beyond visible area

  if (!jobNumber) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Please select a project to view the Dashboard.</div>;
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100%' }}>
      {/* Filter Report Bar - Fixed position at project header level (top right) */}
      {hasActiveFilters && (
        <div style={{ 
          position: 'fixed', 
          top: '120px',  /* Below main app header, at project name level */
          right: '40px', 
          zIndex: 1000, 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          padding: '6px 12px', 
          backgroundColor: '#d1fae5', 
          borderRadius: '6px', 
          border: '1px solid #a7f3d0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <span style={{ fontSize: '12px', color: '#065f46', fontWeight: '600' }}>Filters:</span>
          {activeFilters.map((filter, idx) => (
            <span key={idx} style={{ padding: '2px 8px', backgroundColor: filter.type === 'text' ? '#3b82f6' : '#10b981', color: 'white', borderRadius: '4px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              {filterTemplate.columnLabels[filter.key] || filter.key}: {filter.type === 'values' ? filter.count : (filter.mode + (filter.value ? ` "${filter.value}"` : ''))}
              <span onClick={() => filterTemplate.clearColumnFilter(filter.key)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
            </span>
          ))}
          {searchTerm.trim() && (
            <span style={{ padding: '2px 8px', backgroundColor: '#10b981', color: 'white', borderRadius: '4px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              Search: "{searchTerm}"
              <span onClick={() => setSearchTerm('')} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
            </span>
          )}
          <button onClick={() => { filterTemplate.clearAllFilters(); setSearchTerm(''); }} style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            × Clear
          </button>
        </div>
      )}

      {/* Header Row: Title + Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px', position: 'relative', zIndex: 600 }}>
        {/* Left side: Title, Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2 style={{ margin: 0, color: '#111827', fontSize: '1.25rem', whiteSpace: 'nowrap' }}>
            {dashboardView === 'maestro' ? 'Maestro Dashboard' : 'Forecasting Dashboard'}
          </h2>
          
          {/* Dashboard View Dropdown */}
          <div style={{ position: 'relative' }}>
            <button style={{ ...buttonStyle, backgroundColor: '#f3f4f6' }} onClick={() => setShowViewDropdown(!showViewDropdown)}>
              {dashboardView === 'maestro' ? '📊 Maestro' : '🧭 Forecasting'} ▾
            </button>
            {showViewDropdown && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', overflow: 'hidden', zIndex: 1000, minWidth: '200px' }}>
                <div style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '13px', backgroundColor: dashboardView === 'forecasting' ? '#fef2f2' : 'transparent', display: 'flex', alignItems: 'center', gap: '8px' }}
                  onClick={() => { setDashboardView('forecasting'); setShowViewDropdown(false); setSelectedRows(new Set()); }}
                  onMouseEnter={(e) => { if (dashboardView !== 'forecasting') e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                  onMouseLeave={(e) => { if (dashboardView !== 'forecasting') e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  🧭 Forecasting Dashboard {dashboardView === 'forecasting' && <span style={{ marginLeft: 'auto', color: '#dc3545' }}>✓</span>}
                </div>
                <div style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '13px', backgroundColor: dashboardView === 'maestro' ? '#fef2f2' : 'transparent', display: 'flex', alignItems: 'center', gap: '8px' }}
                  onClick={() => { setDashboardView('maestro'); setShowViewDropdown(false); setSelectedRows(new Set()); }}
                  onMouseEnter={(e) => { if (dashboardView !== 'maestro') e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                  onMouseLeave={(e) => { if (dashboardView !== 'maestro') e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  📊 Maestro Dashboard {dashboardView === 'maestro' && <span style={{ marginLeft: 'auto', color: '#dc3545' }}>✓</span>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Presence indicator */}
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />

        {/* Center/Right: Toolbar Buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '8px 12px 8px 32px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', width: '180px' }} />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>🔍</span>
          </div>

          {/* View $ toggle — Forecast view only; converts all dollar columns for display */}
          {dashboardView === 'forecasting' && masterCurrency && otherCurrency && (
            <div
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
              title={`Values are stored in ${masterCurrency} (the master project currency — foreign-company rows were converted at import). Switch to ${otherCurrency} to read the whole table in ${otherCurrency}; editing is locked while a converted view is on.`}
            >
              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>View $:</span>
              <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                <button
                  onClick={() => setFdCurrencyView('native')}
                  style={{
                    padding: '7px 10px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    backgroundColor: fdCurrencyView === 'native' ? '#1d4ed8' : '#f9fafb',
                    color: fdCurrencyView === 'native' ? '#fff' : '#6b7280',
                  }}
                >
                  {masterCurrency}
                </button>
                <button
                  onClick={() => setFdCurrencyView(otherCurrency)}
                  style={{
                    padding: '7px 10px', fontSize: '12px', fontWeight: '600', border: 'none',
                    borderLeft: '1px solid #d1d5db', cursor: 'pointer',
                    backgroundColor: fdCurrencyView !== 'native' ? '#1d4ed8' : '#f9fafb',
                    color: fdCurrencyView !== 'native' ? '#fff' : '#6b7280',
                  }}
                >
                  {otherCurrency}
                </button>
              </div>
              {fdConvertedView && (
                <span style={{
                  padding: '5px 10px', backgroundColor: '#fefce8', border: '1px solid #fde68a',
                  borderRadius: '6px', fontSize: '11.5px', color: '#92400e', fontWeight: '600',
                }}>
                  × {fdActiveConversion.rate.toFixed(4)} · editing locked
                </span>
              )}
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <button style={buttonStyle} onClick={() => { setShowColumnConfig(!showColumnConfig); setShowExportDropdown(false); }}>📊 Columns</button>
            {showColumnConfig && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '12px', zIndex: 1000, width: '340px', maxHeight: '500px', overflowY: 'auto' }}>
                <ColumnConfigPanel
                  title={`${dashboardView === 'maestro' ? 'Maestro' : 'Forecasting'} Columns`}
                  columns={applyColumnOrder(COLUMNS, dashboardView === 'maestro' ? maestroColOrder.columnOrder : forecastingColOrder.columnOrder)}
                  isVisible={(key) => currentVisibleColumns.has(key)}
                  onToggle={(key) => { setCurrentVisibleColumns(prev => { const next = new Set(prev); if (next.has(key)) { if (next.size > 1) next.delete(key); } else next.add(key); return next; }); }}
                  onMove={dashboardView === 'maestro' ? maestroColOrder.moveColumn : forecastingColOrder.moveColumn}
                  onShowAll={() => setCurrentVisibleColumns(new Set(COLUMNS.map(c => c.key)))}
                />
              </div>
            )}
          </div>

          <button style={buttonStyle} onClick={() => autoFitColumns(data, COLUMNS)} title="Auto-fit column widths to content">↔️ Auto-fit</button>

          <button style={buttonStyle} onClick={handleAddRow}>➕ Add Row</button>
          <button style={{ ...buttonStyle, opacity: selectedRows.size === 0 ? 0.5 : 1 }} onClick={handleDeleteSelected} disabled={selectedRows.size === 0}>🗑️ Delete ({selectedRows.size})</button>

          <button
            style={{ ...buttonStyle, opacity: importDataLoading ? 0.6 : 1 }}
            disabled={importDataLoading}
            onClick={handleImportData}
          >
            {importDataLoading ? '⏳ Importing...' : '📥 Import Data'}
          </button>

          <div style={{ position: 'relative' }}>
            <button style={{ ...buttonStyle, opacity: exportLoading ? 0.6 : 1 }} disabled={exportLoading} onClick={() => { setShowExportDropdown(!showExportDropdown); setShowColumnConfig(false); }}>
              {exportLoading ? '⏳ Exporting...' : '📤 Export ▾'}
            </button>
            {showExportDropdown && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 1000, minWidth: '240px', padding: '12px 16px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', marginBottom: '6px' }}>Rows</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', marginBottom: '4px' }}>
                  <input type="radio" name="fdExportRows" checked={exportRowsScope === 'filtered'} onChange={() => setExportRowsScope('filtered')} style={{ margin: 0 }} />
                  Filtered — as displayed ({filteredData.length})
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', marginBottom: '10px' }}>
                  <input type="radio" name="fdExportRows" checked={exportRowsScope === 'all'} onChange={() => setExportRowsScope('all')} style={{ margin: 0 }} />
                  Complete table ({computedData.length})
                </label>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', marginBottom: '6px' }}>Columns</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', marginBottom: '4px' }}>
                  <input type="radio" name="fdExportCols" checked={exportColsScope === 'visible'} onChange={() => setExportColsScope('visible')} style={{ margin: 0 }} />
                  Visible columns
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', marginBottom: '12px' }}>
                  <input type="radio" name="fdExportCols" checked={exportColsScope === 'all'} onChange={() => setExportColsScope('all')} style={{ margin: 0 }} />
                  All columns
                </label>
                <button
                  onClick={() => handleExport({ columns: exportColsScope, rows: exportRowsScope })}
                  style={{ width: '100%', padding: '8px 12px', backgroundColor: '#1d4ed8', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                >
                  ⬇ Export to Excel
                </button>
              </div>
            )}
          </div>

          <button style={buttonStyle} onClick={() => setAuditLogOpen(true)} title="Change history: edits, imports, finalizes and deletes on this dashboard">
            📜 Audit
          </button>

          <button style={buttonStyle} onClick={() => { fetchData(); }} title="Refresh data">
            🔄 Refresh
          </button>

          <button style={{ ...buttonStyle, backgroundColor: analyticsOpen ? '#dc3545' : 'white', color: analyticsOpen ? 'white' : '#374151', borderColor: analyticsOpen ? '#dc3545' : '#d1d5db' }} 
            onClick={async () => {
              if (!analyticsOpen) {
                // Fetch EVM history when opening analytics
                try {
                  const evmRes = await authenticatedApiCall(instance, accounts, 'GET', `/forecasting-dashboard/${jobNumber}/evm-history`);
                  if (evmRes.data.success) {
                    setEvmHistory(evmRes.data.data || []);
                  }
                } catch (err) {
                  console.error('Error fetching EVM history:', err);
                  setEvmHistory([]);
                }
              }
              setAnalyticsOpen(!analyticsOpen);
            }}>
            📈 Analytics
          </button>

          <button style={buttonStyle} onClick={() => setHelpOpen(true)}>
            ❓ Help
          </button>

          {/* Finalize Button - Only for Forecasting view */}
          {dashboardView === 'forecasting' && (
            <button 
              style={{ ...buttonStyle, border: '1px solid #d1d5db' }} 
              onClick={handleFinalizeClick}
              title="Copy current % Complete, ETC, FAC, Revised Selling Bud to previous period columns">
              ✓ Finalize
            </button>
          )}

        </div>
      </div>

      {/* ANALYTICS MODAL - Popup window like Time Tracker */}
      {analyticsOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
                📈 {dashboardView === 'maestro' ? 'Maestro' : 'Forecasting'} Analytics
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => { setShowAnalyticsEmailModal(true); setAnalyticsEmailResult(null); }}
                  disabled={!analytics}
                  title="Email report as PDF"
                  style={{ ...buttonStyle, opacity: analytics ? 1 : 0.5, cursor: analytics ? 'pointer' : 'not-allowed' }}
                >
                  📧 Email
                </button>
                <button
                  onClick={handleAnalyticsDownloadPdf}
                  disabled={!analytics || analyticsExporting === 'pdf'}
                  title="Download report as PDF"
                  style={{ ...buttonStyle, opacity: analytics ? 1 : 0.5, cursor: analytics && analyticsExporting !== 'pdf' ? 'pointer' : 'not-allowed' }}
                >
                  {analyticsExporting === 'pdf' ? '…' : '📥'} PDF
                </button>
                <button
                  onClick={handleAnalyticsDownloadExcel}
                  disabled={!analytics || analyticsExporting === 'excel'}
                  title="Download report as Excel"
                  style={{ ...buttonStyle, opacity: analytics ? 1 : 0.5, cursor: analytics && analyticsExporting !== 'excel' ? 'pointer' : 'not-allowed' }}
                >
                  {analyticsExporting === 'excel' ? '…' : '📊'} Excel
                </button>
                <button
                  onClick={() => setAnalyticsOpen(false)}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              {!analytics ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px' }}>
                  No data available for analytics. Add some rows first.
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontSize: '13px', color: '#0369a1', marginBottom: '4px', fontWeight: '500' }}>Total Rows</div>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#0c4a6e' }}>{analytics.rowCount}</div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <div style={{ fontSize: '13px', color: '#15803d', marginBottom: '4px', fontWeight: '500' }}>Original Budget</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#14532d' }}>${(analytics.totals.orig_bud || 0).toLocaleString()}</div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: '#fefce8', borderRadius: '8px', border: '1px solid #fef08a' }}>
                      <div style={{ fontSize: '13px', color: '#a16207', marginBottom: '4px', fontWeight: '500' }}>Revised Budget</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#713f12' }}>${(analytics.totals.revised_bud || 0).toLocaleString()}</div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                      <div style={{ fontSize: '13px', color: '#7c3aed', marginBottom: '4px', fontWeight: '500' }}>Actual</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#581c87' }}>${(analytics.totals.actual || 0).toLocaleString()}</div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: analytics.budgetVariance >= 0 ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', border: `1px solid ${analytics.budgetVariance >= 0 ? '#fecaca' : '#bbf7d0'}` }}>
                      <div style={{ fontSize: '13px', color: analytics.budgetVariance >= 0 ? '#dc2626' : '#16a34a', marginBottom: '4px', fontWeight: '500' }}>Budget Variance</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: analytics.budgetVariance >= 0 ? '#991b1b' : '#14532d' }}>
                        {analytics.budgetVariance >= 0 ? '+' : ''}${analytics.budgetVariance.toLocaleString()}
                        <span style={{ fontSize: '14px', marginLeft: '8px' }}>({analytics.budgetVariancePercent.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: analytics.forecastedMarginPercent >= 0 ? '#ecfdf5' : '#fef2f2', borderRadius: '8px', border: `1px solid ${analytics.forecastedMarginPercent >= 0 ? '#a7f3d0' : '#fecaca'}` }}>
                      <div style={{ fontSize: '13px', color: analytics.forecastedMarginPercent >= 0 ? '#059669' : '#dc2626', marginBottom: '4px', fontWeight: '500' }}>Forecasted Margin</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: analytics.forecastedMarginPercent >= 0 ? '#065f46' : '#991b1b' }}>
                        {analytics.forecastedMarginPercent >= 0 ? '+' : ''}{analytics.forecastedMarginPercent.toFixed(1)}%
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>(Rev Selling Bud - FAC) / FAC</div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: analytics.originalMarginPercent >= 0 ? '#ecfdf5' : '#fef2f2', borderRadius: '8px', border: `1px solid ${analytics.originalMarginPercent >= 0 ? '#a7f3d0' : '#fecaca'}` }}>
                      <div style={{ fontSize: '13px', color: analytics.originalMarginPercent >= 0 ? '#059669' : '#dc2626', marginBottom: '4px', fontWeight: '500' }}>Original Margin</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: analytics.originalMarginPercent >= 0 ? '#065f46' : '#991b1b' }}>
                        {analytics.originalMarginPercent >= 0 ? '+' : ''}{(analytics.originalMarginPercent || 0).toFixed(1)}%
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Original Selling (90001) / Orig Bud</div>
                    </div>
                  </div>

                  {/* Contract Value Breakdown */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                      <div style={{ fontSize: '13px', color: '#0369a1', marginBottom: '4px', fontWeight: '500' }}>Total Contract Value</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#0c4a6e' }}>${(analytics.totalContractValue || 0).toLocaleString()}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>All Revised Selling Bud</div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <div style={{ fontSize: '13px', color: '#15803d', marginBottom: '4px', fontWeight: '500' }}>Total Base Contract Value</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#14532d' }}>${(analytics.totalBaseContractValue || 0).toLocaleString()}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Contract type rows only</div>
                    </div>
                    <div style={{ padding: '20px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
                      <div style={{ fontSize: '13px', color: '#a16207', marginBottom: '4px', fontWeight: '500' }}>Total Extra Value</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#713f12' }}>${(analytics.totalExtraValue || 0).toLocaleString()}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Extra type rows only</div>
                    </div>
                  </div>

                  {/* EVM Metrics Section */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>📊</span> Earned Value Management (EVM) Metrics
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                      {/* CPI */}
                      <div style={{ padding: '16px', backgroundColor: analytics.CPI >= 1 ? '#ecfdf5' : '#fef2f2', borderRadius: '8px', border: `1px solid ${analytics.CPI >= 1 ? '#a7f3d0' : '#fecaca'}` }}>
                        <div style={{ fontSize: '12px', color: analytics.CPI >= 1 ? '#059669' : '#dc2626', marginBottom: '4px', fontWeight: '500' }}>CPI (Cost Performance)</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: analytics.CPI >= 1 ? '#065f46' : '#991b1b' }}>
                          {(analytics.CPI || 0).toFixed(2)}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>{analytics.CPI >= 1 ? 'Under budget' : 'Over budget'}</div>
                      </div>
                      {/* SPI */}
                      <div style={{ padding: '16px', backgroundColor: analytics.SPI >= 1 ? '#ecfdf5' : '#fef2f2', borderRadius: '8px', border: `1px solid ${analytics.SPI >= 1 ? '#a7f3d0' : '#fecaca'}` }}>
                        <div style={{ fontSize: '12px', color: analytics.SPI >= 1 ? '#059669' : '#dc2626', marginBottom: '4px', fontWeight: '500' }}>SPI (Schedule Performance)</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: analytics.SPI >= 1 ? '#065f46' : '#991b1b' }}>
                          {(analytics.SPI || 0).toFixed(2)}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>{analytics.SPI >= 1 ? 'Ahead of schedule' : 'Behind schedule'}</div>
                      </div>
                      {/* CV */}
                      <div style={{ padding: '16px', backgroundColor: analytics.CV >= 0 ? '#ecfdf5' : '#fef2f2', borderRadius: '8px', border: `1px solid ${analytics.CV >= 0 ? '#a7f3d0' : '#fecaca'}` }}>
                        <div style={{ fontSize: '12px', color: analytics.CV >= 0 ? '#059669' : '#dc2626', marginBottom: '4px', fontWeight: '500' }}>CV (Cost Variance)</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: analytics.CV >= 0 ? '#065f46' : '#991b1b' }}>
                          {analytics.CV >= 0 ? '+' : ''}${(analytics.CV || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>EV - AC</div>
                      </div>
                      {/* SV */}
                      <div style={{ padding: '16px', backgroundColor: analytics.SV >= 0 ? '#ecfdf5' : '#fef2f2', borderRadius: '8px', border: `1px solid ${analytics.SV >= 0 ? '#a7f3d0' : '#fecaca'}` }}>
                        <div style={{ fontSize: '12px', color: analytics.SV >= 0 ? '#059669' : '#dc2626', marginBottom: '4px', fontWeight: '500' }}>SV (Schedule Variance)</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: analytics.SV >= 0 ? '#065f46' : '#991b1b' }}>
                          {analytics.SV >= 0 ? '+' : ''}${(analytics.SV || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>EV - PV</div>
                      </div>
                    </div>
                    {/* EVM Base Values */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '16px' }}>
                      <div style={{ padding: '12px 16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                        <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: '500' }}>PV (Planned Value)</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#0c4a6e' }}>${(analytics.PV || 0).toLocaleString()}</div>
                      </div>
                      <div style={{ padding: '12px 16px', backgroundColor: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                        <div style={{ fontSize: '11px', color: '#7c3aed', fontWeight: '500' }}>AC (Actual Cost)</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#581c87' }}>${(analytics.AC || 0).toLocaleString()}</div>
                      </div>
                      <div style={{ padding: '12px 16px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                        <div style={{ fontSize: '11px', color: '#059669', fontWeight: '500' }}>EV (Earned Value)</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#065f46' }}>${(analytics.EV || 0).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>

                  {/* EVM S-Curve Chart */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span><span style={{ fontSize: '18px' }}>📈</span> EVM S-Curve</span>
                      {evmHistory.length === 0 && (
                        <button 
                          onClick={async () => {
                            try {
                              const res = await authenticatedApiCall(instance, accounts, 'POST', `/forecasting-dashboard/${jobNumber}/generate-demo-evm`, {
                                project_duration_months: 15,
                                total_budget: analytics.totals?.orig_bud || 27326168.22
                              }, syncHeaders());
                              if (res.data.success) {
                                setEvmHistory(res.data.data || []);
                                alert('Generated 15 months of demo EVM data for visualization.');
                              }
                            } catch (err) {
                              console.error('Error generating demo data:', err);
                              alert('Failed to generate demo data: ' + err.message);
                            }
                          }}
                          style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: '#0ea5e9', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Generate Demo Data
                        </button>
                      )}
                    </h4>
                    {evmHistory.length > 0 ? (
                      <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={evmHistory} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="period_month" 
                              tick={{ fontSize: 11 }} 
                              label={{ value: 'Month', position: 'bottom', offset: -5, fontSize: 12 }}
                            />
                            <YAxis 
                              tick={{ fontSize: 10 }} 
                              tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`}
                              label={{ value: 'Cumulative Value ($)', angle: -90, position: 'insideLeft', fontSize: 11 }}
                            />
                            <Tooltip 
                              formatter={(value, name) => [`$${Number(value).toLocaleString()}`, name]}
                              labelFormatter={(label) => `Month ${label}`}
                            />
                            <Legend verticalAlign="top" height={36} />
                            <Line 
                              type="monotone" 
                              dataKey="planned_value" 
                              name="PV (Planned Value)" 
                              stroke="#0369a1" 
                              strokeWidth={2}
                              dot={{ r: 3 }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="actual_cost" 
                              name="AC (Actual Cost)" 
                              stroke="#7c3aed" 
                              strokeWidth={2}
                              dot={{ r: 3 }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="earned_value" 
                              name="EV (Earned Value)" 
                              stroke="#059669" 
                              strokeWidth={2}
                              dot={{ r: 3 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '8px', fontSize: '11px', color: '#6b7280' }}>
                          <span><span style={{ color: '#0369a1', fontWeight: '600' }}>—</span> PV: Budget distribution over time</span>
                          <span><span style={{ color: '#7c3aed', fontWeight: '600' }}>—</span> AC: Actual costs incurred</span>
                          <span><span style={{ color: '#059669', fontWeight: '600' }}>—</span> EV: Value of work completed</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '40px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px dashed #0ea5e9', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#0369a1', fontWeight: '500' }}>No EVM History Data Available</p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#0369a1' }}>
                          EVM data is recorded when you finalize periods. Click "Generate Demo Data" above to create sample data for visualization.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Charts Section - keeping simplified version */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                    {/* Budget Comparison Bar Chart */}
                    <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '0.95rem' }}>Budget vs Actual Comparison</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={[
                          { name: 'PV (Orig Bud)', value: analytics.PV || 0, fill: '#0369a1' },
                          { name: 'AC (Actual)', value: analytics.AC || 0, fill: '#7c3aed' },
                          { name: 'EV (Earned)', value: analytics.EV || 0, fill: '#059669' },
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} />
                          <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {[
                              { fill: '#0369a1' },
                              { fill: '#7c3aed' },
                              { fill: '#059669' },
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Performance Indices Chart */}
                    <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '0.95rem' }}>Performance Indices</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={[
                          { name: 'CPI', value: analytics.CPI || 0, fill: analytics.CPI >= 1 ? '#059669' : '#dc2626' },
                          { name: 'SPI', value: analytics.SPI || 0, fill: analytics.SPI >= 1 ? '#059669' : '#dc2626' },
                        ]} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" domain={[0, 2]} tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={40} />
                          <Tooltip formatter={(v) => v.toFixed(2)} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            <Cell fill={analytics.CPI >= 1 ? '#059669' : '#dc2626'} />
                            <Cell fill={analytics.SPI >= 1 ? '#059669' : '#dc2626'} />
                          </Bar>
                          {/* Reference line at 1.0 */}
                        </BarChart>
                      </ResponsiveContainer>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px', textAlign: 'center' }}>
                        Target: 1.0 (green = good, red = needs attention)
                      </div>
                    </div>
                  </div>

                  {/* Detailed Totals - 2x2 Comparative Layout */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>
                      Column Totals - Comparative View
                    </h4>
                    {/* Row 1: Budget Comparisons */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '16px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px', fontWeight: '500' }}>Orig Bud</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#78350f' }}>${(analytics.totals.orig_bud || 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px', fontWeight: '500' }}>Revised Bud</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#78350f' }}>${(analytics.totals.revised_bud || 0).toLocaleString()}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '16px', backgroundColor: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#7c3aed', marginBottom: '4px', fontWeight: '500' }}>Actual</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#581c87' }}>${(analytics.totals.actual || 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#7c3aed', marginBottom: '4px', fontWeight: '500' }}>Comm Costs</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#581c87' }}>${(analytics.totals.comm_costs || 0).toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                    {/* Row 2: ETC vs FAC */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '16px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#059669', marginBottom: '4px', fontWeight: '500' }}>ETC (Est. Balance)</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#065f46' }}>${(analytics.totals.etc_estimate_to_complete || 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#059669', marginBottom: '4px', fontWeight: '500' }}>ETC Prev</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#065f46' }}>${(analytics.totals.etc_prev || 0).toLocaleString()}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#0369a1', marginBottom: '4px', fontWeight: '500' }}>FAC (Est. Cost)</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#0c4a6e' }}>${(analytics.totals.fac_forecast_at_completion || 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#0369a1', marginBottom: '4px', fontWeight: '500' }}>FAC Prev</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#0c4a6e' }}>${(analytics.totals.fac_prev || 0).toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                    {/* Row 3: Selling vs Profit */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#dc2626', marginBottom: '4px', fontWeight: '500' }}>Bud Bal</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#991b1b' }}>${(analytics.totals.bud_bal || 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#dc2626', marginBottom: '4px', fontWeight: '500' }}>Fore Var $</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#991b1b' }}>${(analytics.totals.fore_var_dollars || 0).toLocaleString()}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '4px', fontWeight: '500' }}>Revised Selling Bud</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#14532d' }}>${(analytics.totals.revised_selling_bud || 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '4px', fontWeight: '500' }}>Revised Selling Bud Prev</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#14532d' }}>${(analytics.totals.revised_selling_bud_prev || 0).toLocaleString()}</div>
                        </div>
                        <div style={{ padding: '8px' }}>
                          <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '4px', fontWeight: '500' }}>Forecasted Profit</div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#14532d' }}>${(analytics.totals.estimated_profit_at_completion || 0).toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Percent Averages - 2x2 Layout */}
                  {Object.keys(analytics.percentAvgs).length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 16px 0', color: '#374151', fontSize: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>
                        Percentage Averages
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <div style={{ padding: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: '500' }}>% Complete</div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>{(analytics.percentAvgs.percent_complete || 0).toFixed(1)}%</div>
                          </div>
                          <div style={{ padding: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: '500' }}>% Complete Prev</div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>{(analytics.percentAvgs.percent_complete_prev || 0).toFixed(1)}%</div>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <div style={{ padding: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: '500' }}>% Margin</div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: (analytics.percentAvgs.percent_margin || 0) >= 0 ? '#059669' : '#dc2626' }}>{(analytics.percentAvgs.percent_margin || 0).toFixed(1)}%</div>
                          </div>
                          <div style={{ padding: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: '500' }}>Fore Var %</div>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: (analytics.percentAvgs.fore_var_percent || 0) >= 0 ? '#059669' : '#dc2626' }}>{(analytics.percentAvgs.fore_var_percent || 0).toFixed(1)}%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showAnalyticsEmailModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAnalyticsEmailModal(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: '#1e293b' }}>📧 Email Analytics Report</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 0.75rem' }}>
              Enter email addresses separated by commas. A PDF report will be attached.
            </p>
            <textarea
              value={analyticsEmailAddresses}
              onChange={(e) => setAnalyticsEmailAddresses(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
              style={{ width: '100%', minHeight: '80px', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            {analyticsEmailResult && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem',
                backgroundColor: analyticsEmailResult.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: analyticsEmailResult.type === 'success' ? '#059669' : '#dc2626' }}>
                {analyticsEmailResult.message}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button onClick={() => { setShowAnalyticsEmailModal(false); setAnalyticsEmailResult(null); }}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>
                Cancel
              </button>
              <button onClick={handleAnalyticsSendEmail} disabled={analyticsEmailSending || !analyticsEmailAddresses.trim()}
                style={{ padding: '0.5rem 1rem', backgroundColor: analyticsEmailSending ? '#9ca3af' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: analyticsEmailSending ? 'wait' : 'pointer', fontSize: '0.85rem', fontWeight: '500' }}>
                {analyticsEmailSending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HELP MODAL */}
      {helpOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '95%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#0369a1'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'white' }}>
                ❓ Forecasting Dashboard Help
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ListenButton compact dark guideId="forecasting-dashboard-help" title="Forecasting Dashboard Help" accent="#0369a1" />
                <button
                  onClick={() => { setHelpOpen(false); setHelpTab('guide'); }}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Tab Bar — flexShrink + zIndex keep tabs above scrollable body */}
            <div style={{ display: 'flex', flexShrink: 0, position: 'relative', zIndex: 3, borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
              {[
                { id: 'guide', label: '📖 User Guide' },
                { id: 'flowchart', label: '🔀 Workflow Flowchart' },
                { id: 'tour', label: '🎬 Video Tour' },
              ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setHelpTab(t.id)}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  flexShrink: 0,
                  borderBottom: helpTab === t.id ? '3px solid #0369a1' : '3px solid transparent',
                  backgroundColor: helpTab === t.id ? 'white' : 'transparent',
                  color: helpTab === t.id ? '#0369a1' : '#6b7280',
                  fontWeight: helpTab === t.id ? '600' : '400',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  zIndex: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {t.label}
              </button>
              ))}
            </div>
            
            {/* Modal Body */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              
              {/* FLOWCHART TAB */}
              {helpTab === 'flowchart' && (
                <div style={{ padding: '20px' }}>
                  <h3 style={{ margin: '0 0 24px 0', textAlign: 'center', color: '#111827' }}>Forecasting Dashboard Workflow</h3>
                  
                  {/* Flowchart Container */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    
                    {/* Step 1: Import Maestro */}
                    <div style={{ padding: '16px 32px', backgroundColor: '#dbeafe', borderRadius: '8px', border: '2px solid #3b82f6', textAlign: 'center', minWidth: '280px' }}>
                      <div style={{ fontSize: '20px', marginBottom: '4px' }}>📥</div>
                      <div style={{ fontWeight: '600', color: '#1e40af' }}>1. Import Maestro Excel</div>
                      <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>Upload Maestro export file</div>
                    </div>
                    
                    <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓</div>
                    
                    {/* Step 2: Auto-populate fields */}
                    <div style={{ padding: '16px 32px', backgroundColor: '#dcfce7', borderRadius: '8px', border: '2px solid #22c55e', textAlign: 'center', minWidth: '280px' }}>
                      <div style={{ fontSize: '20px', marginBottom: '4px' }}>⚡</div>
                      <div style={{ fontWeight: '600', color: '#15803d' }}>2. Auto-Populate Fields</div>
                      <div style={{ fontSize: '12px', color: '#16a34a', marginTop: '4px' }}>Activity, Budgets, Actual, Comm Costs, Bud Bal</div>
                    </div>
                    
                    <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓</div>
                    
                    {/* Step 3: Manual Entry */}
                    <div style={{ padding: '16px 32px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '2px solid #f59e0b', textAlign: 'center', minWidth: '280px' }}>
                      <div style={{ fontSize: '20px', marginBottom: '4px' }}>✏️</div>
                      <div style={{ fontWeight: '600', color: '#b45309' }}>3. Manual Entry</div>
                      <div style={{ fontSize: '12px', color: '#d97706', marginTop: '4px' }}>% Complete, ETC, Contract vs Extra</div>
                    </div>
                    
                    <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓</div>
                    
                    {/* Step 4: Auto-Calculate */}
                    <div style={{ padding: '16px 32px', backgroundColor: '#e0e7ff', borderRadius: '8px', border: '2px solid #6366f1', textAlign: 'center', minWidth: '280px' }}>
                      <div style={{ fontSize: '20px', marginBottom: '4px' }}>🔄</div>
                      <div style={{ fontWeight: '600', color: '#4338ca' }}>4. Auto-Calculate (Real-time)</div>
                      <div style={{ fontSize: '12px', color: '#6366f1', marginTop: '4px' }}>FAC, Forecasted Profit, % Margin, Fore Var</div>
                    </div>
                    
                    <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓</div>
                    
                    {/* Step 5: Review & Analyze */}
                    <div style={{ padding: '16px 32px', backgroundColor: '#fae8ff', borderRadius: '8px', border: '2px solid #c026d3', textAlign: 'center', minWidth: '280px' }}>
                      <div style={{ fontSize: '20px', marginBottom: '4px' }}>📊</div>
                      <div style={{ fontWeight: '600', color: '#a21caf' }}>5. Review & Analyze</div>
                      <div style={{ fontSize: '12px', color: '#c026d3', marginTop: '4px' }}>Analytics: Excel, PDF, Email</div>
                    </div>
                    
                    <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓</div>
                    
                    {/* Step 6: Finalize */}
                    <div style={{ padding: '16px 32px', backgroundColor: '#fee2e2', borderRadius: '8px', border: '2px solid #ef4444', textAlign: 'center', minWidth: '280px' }}>
                      <div style={{ fontSize: '20px', marginBottom: '4px' }}>✅</div>
                      <div style={{ fontWeight: '600', color: '#b91c1c' }}>6. Finalize Period</div>
                      <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>Saves to Previous Period, Downloads PDF</div>
                    </div>
                  </div>
                  
                  {/* Formula Flow */}
                  <div style={{ marginTop: '32px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>📐 Calculation Flow</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ padding: '6px 12px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '12px', color: '#1e40af' }}>% Complete</span>
                      <span style={{ color: '#9ca3af' }}>+</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '12px', color: '#1e40af' }}>ETC</span>
                      <span style={{ color: '#9ca3af' }}>+</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '12px', color: '#1e40af' }}>Actual</span>
                      <span style={{ color: '#9ca3af' }}>→</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#e0e7ff', borderRadius: '4px', fontSize: '12px', color: '#4338ca', fontWeight: '600' }}>FAC</span>
                      <span style={{ color: '#9ca3af' }}>→</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#fae8ff', borderRadius: '4px', fontSize: '12px', color: '#a21caf', fontWeight: '600' }}>Forecasted Profit</span>
                      <span style={{ color: '#9ca3af' }}>→</span>
                      <span style={{ padding: '6px 12px', backgroundColor: '#fee2e2', borderRadius: '4px', fontSize: '12px', color: '#b91c1c', fontWeight: '600' }}>Fore Var $/%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* GUIDE TAB */}
              {helpTab === 'guide' && (
              <>
              {/* Workflow Section */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>1</span>
                  Workflow Overview
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', textAlign: 'center', border: '1px solid #bae6fd' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>📥</div>
                    <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '4px' }}>Import Maestro</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Upload Maestro Excel file</div>
                  </div>
                  <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', textAlign: 'center', border: '1px solid #bbf7d0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>→</div>
                    <div style={{ fontWeight: '600', color: '#15803d' }}>Auto-Feed</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Calculates formulas automatically</div>
                  </div>
                  <div style={{ padding: '16px', backgroundColor: '#fef3c7', borderRadius: '8px', textAlign: 'center', border: '1px solid #fcd34d' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>🧭</div>
                    <div style={{ fontWeight: '600', color: '#a16207', marginBottom: '4px' }}>Forecasting Dashboard</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Analyze & manage forecasts</div>
                  </div>
                </div>
                <div style={{ padding: '12px 16px', backgroundColor: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', fontSize: '13px', color: '#581c87', lineHeight: '1.6' }}>
                  <strong>Share Analytics:</strong> Open <strong>📈 Analytics</strong>, then use <strong>Excel</strong>, <strong>PDF</strong>, or <strong>Email</strong> in the header — same pattern as piping progress analytics. Excel has Summary, EVM History, and Data sheets. Email attaches the PDF.
                </div>
              </div>

              {/* Data Mapping Section */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>2</span>
                  Data Mapping (Maestro → Forecasting)
                </h3>
                <div style={{ marginBottom: '12px', padding: '12px 16px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '13px', color: '#1e3a5f', lineHeight: '1.6' }}>
                  <strong>Rolled up by Operation Code:</strong> Maestro lists each operation code as several cost-type lines (Labour, Material, Equipment, Subcontract…). On import these are combined into a <strong>single row per operation code</strong>, and the dollar columns (Orig Bud, Revised Bud, Actual, Comm Costs, Revised Selling Bud, Bud Bal) are <strong>summed</strong>. This prevents duplicated extra rows and double-counted totals.
                </div>
                <div style={{ marginBottom: '12px', padding: '12px 16px', backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', fontSize: '13px', color: '#065f46', lineHeight: '1.6' }}>
                  <strong>💱 Cross-border jobs (CAD + USD):</strong> the rollup also merges rows from <strong>every company (prefix)</strong> — that is why there is no Prefix column here. Before rolling up, the import <strong>converts each company's dollars to the master project currency</strong> at the current exchange rate (same "swap first, then count" as the Consolidated report). The rate used is stamped on the import and shown in the success message. Use the <strong>View $</strong> toggle in the toolbar to read the table in the other currency (display-only; editing locks). See <strong>Maestro → Help</strong> for the currency schematic.
                </div>
                <div style={{ marginBottom: '12px', padding: '12px 16px', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', fontSize: '13px', color: '#3730a3', lineHeight: '1.6' }}>
                  <strong>❄️ Re-imports keep your frozen data:</strong> <strong>📥 Import Data</strong> lets you pick <strong>which Maestro import (batch)</strong> to load — the latest is preselected. Re-importing replaces the Maestro columns and resets the current % Complete / ETC, but the frozen <strong>Previous Period</strong> columns (from ✓ Finalize) and your <strong>Notes</strong> are carried over to the new rows, matched by operation code. Finalize first if you want the current values snapshotted before they reset.
                </div>
                <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Field</th>
                        <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>Operation Code</td><td style={{ padding: '8px', color: '#0369a1' }}>Calculated: LEFT(Activity, 5) — one row per operation code</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>Activity</td><td style={{ padding: '8px', color: '#15803d' }}>✓ From Maestro</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>Cost Type</td><td style={{ padding: '8px', color: '#6b7280' }}>Collapsed when rolled up (blank on combined rows)</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>Orig Bud, Revised Bud, Actual</td><td style={{ padding: '8px', color: '#15803d' }}>✓ From Maestro (summed per operation code)</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>Comm Costs, Revised Selling Bud</td><td style={{ padding: '8px', color: '#15803d' }}>✓ From Maestro (summed per operation code)</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>Bud Bal</td><td style={{ padding: '8px', color: '#15803d' }}>✓ From Maestro (summed per operation code)</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>Contract vs Extra</td><td style={{ padding: '8px', color: '#6b7280' }}>Manual entry ("contract" or "extra")</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>% Complete</td><td style={{ padding: '8px', color: '#6b7280' }}>Manual entry (triggers recalculation)</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>ETC</td><td style={{ padding: '8px', color: '#6b7280' }}>Manual entry (triggers recalculation)</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>FAC, Forecasted Profit, % Margin</td><td style={{ padding: '8px', color: '#0369a1' }}>Calculated (see formulas below)</td></tr>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '8px' }}>Fore Var $, Fore Var %</td><td style={{ padding: '8px', color: '#0369a1' }}>Calculated (see formulas below)</td></tr>
                      <tr><td style={{ padding: '8px' }}>Note</td><td style={{ padding: '8px', color: '#6b7280' }}>Manual entry</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Priority Formula Sequence Section */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: '#dc3545', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>3</span>
                  Priority Formula Sequence
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* ETC Priority */}
                  <div style={{ padding: '16px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                    <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '8px', fontSize: '14px' }}>ETC (Estimate to Complete) Priority:</div>
                    <div style={{ fontSize: '13px', color: '#78350f', lineHeight: '1.8' }}>
                      <div><strong>1.</strong> If % Complete &gt; 0: <code style={{ backgroundColor: '#fde68a', padding: '2px 6px', borderRadius: '4px' }}>ETC = FAC - Comm Costs</code></div>
                      <div><strong>2.</strong> If % Complete = 0: <strong>Manual entry required</strong></div>
                      <div style={{ marginTop: '4px', fontStyle: 'italic', color: '#b45309' }}>Calculation refreshes automatically after changes</div>
                    </div>
                  </div>
                  
                  {/* FAC Priority */}
                  <div style={{ padding: '16px', backgroundColor: '#dbeafe', borderRadius: '8px', border: '1px solid #93c5fd' }}>
                    <div style={{ fontWeight: '700', color: '#1e40af', marginBottom: '8px', fontSize: '14px' }}>FAC (Forecast at Completion) Priority:</div>
                    <div style={{ fontSize: '13px', color: '#1e3a5f', lineHeight: '1.8' }}>
                      <div><strong>1.</strong> If % Complete &gt; 0: <code style={{ backgroundColor: '#bfdbfe', padding: '2px 6px', borderRadius: '4px' }}>FAC = Comm Costs / % Complete</code></div>
                      <div><strong>2.</strong> If % Complete = 0 and ETC &gt; 0: <code style={{ backgroundColor: '#bfdbfe', padding: '2px 6px', borderRadius: '4px' }}>FAC = Comm Costs + ETC</code></div>
                      <div><strong>3.</strong> If no % Complete and no ETC: <code style={{ backgroundColor: '#bfdbfe', padding: '2px 6px', borderRadius: '4px' }}>FAC = Orig Bud</code> (original estimated cost)</div>
                      <div style={{ marginTop: '4px', fontStyle: 'italic', color: '#3b82f6' }}>Calculation refreshes automatically after changes</div>
                    </div>
                  </div>
                  
                  {/* ETC Override */}
                  <div style={{ padding: '16px', backgroundColor: '#dcfce7', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <div style={{ fontWeight: '700', color: '#166534', marginBottom: '8px', fontSize: '14px' }}>ETC Override Logic:</div>
                    <div style={{ fontSize: '13px', color: '#14532d', lineHeight: '1.8' }}>
                      <div>If user manually enters a <strong>non-zero</strong> ETC (regardless of % Complete):</div>
                      <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                        <div><code style={{ backgroundColor: '#bbf7d0', padding: '2px 6px', borderRadius: '4px' }}>FAC = Comm Costs + ETC</code></div>
                        <div><code style={{ backgroundColor: '#bbf7d0', padding: '2px 6px', borderRadius: '4px' }}>% Complete = Comm Costs / FAC</code></div>
                      </div>
                    </div>
                  </div>

                  {/* Zero Reset */}
                  <div style={{ padding: '16px', backgroundColor: '#e0f2fe', borderRadius: '8px', border: '1px solid #7dd3fc' }}>
                    <div style={{ fontWeight: '700', color: '#0369a1', marginBottom: '8px', fontSize: '14px' }}>Zero = Reset (one edit):</div>
                    <div style={{ fontSize: '13px', color: '#0c4a6e', lineHeight: '1.8' }}>
                      <div>Typing <strong>0</strong> into either <strong>% Complete</strong> or <strong>ETC</strong> resets the whole row in one edit:
                        both drivers go to 0, override mode turns off, and <code style={{ backgroundColor: '#bae6fd', padding: '2px 6px', borderRadius: '4px' }}>FAC = Orig Bud</code>.</div>
                      <div style={{ marginTop: '4px', fontStyle: 'italic' }}>To mark an activity finished instead, enter <strong>% Complete = 100</strong> (FAC becomes the committed costs).</div>
                    </div>
                  </div>
                  
                  {/* Revised Selling Bud Rules */}
                  <div style={{ padding: '16px', backgroundColor: '#fce7f3', borderRadius: '8px', border: '1px solid #f9a8d4' }}>
                    <div style={{ fontWeight: '700', color: '#9d174d', marginBottom: '8px', fontSize: '14px' }}>Revised Selling Bud Rules:</div>
                    <div style={{ fontSize: '13px', color: '#831843', lineHeight: '1.8' }}>
                      <div><strong>Default:</strong> Value from Maestro</div>
                      <div><strong>PM Override:</strong></div>
                      <div style={{ marginLeft: '16px' }}>
                        <div>• <strong>Contract</strong> rows: Override is <span style={{ color: '#dc2626', fontWeight: '600' }}>BLOCKED</span> (click prevented)</div>
                        <div>• <strong>Extra</strong> rows: Override is <span style={{ color: '#16a34a', fontWeight: '600' }}>AVAILABLE</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Formulas Section */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>4</span>
                  Calculated Fields & Formulas
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #0369a1' }}>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Operation Code</div>
                    <code style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>= LEFT(Activity, 5)</code>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #0369a1' }}>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>FAC (Forecast at Completion)</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                      <code style={{ backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginBottom: '4px' }}>IF % Complete &gt; 0 → FAC = Comm Costs / % Complete</code><br/>
                      <code style={{ backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginBottom: '4px' }}>IF % Complete = 0 and ETC &gt; 0 → FAC = Comm Costs + ETC</code><br/>
                      <code style={{ backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>IF no % Complete and no ETC → FAC = Orig Bud</code>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #0369a1' }}>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Forecasted Profit (Estimated Profit at Completion)</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
                      <code style={{ backgroundColor: '#fee2e2', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginBottom: '4px' }}>IF Contract = "contract" → 0 (always zero)</code><br/>
                      <code style={{ backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginBottom: '4px' }}>IF Contract = "revenue" → Revised Selling - FAC</code><br/>
                      <code style={{ backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>IF Contract = "extra" → Revised Selling Bud - FAC</code>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #0369a1' }}>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>% Margin</div>
                    <code style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>= Forecasted Profit / FAC</code>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #059669' }}>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Forecasted Margin (Analytics)</div>
                    <code style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>= (Revised Selling Bud - FAC) / FAC × 100</code>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #059669' }}>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Original Margin (Analytics)</div>
                    <code style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>= (Original Selling from Op 90001 - Orig Bud) / Orig Bud × 100</code>
                    <div style={{ fontSize: '11px', color: '#059669', marginTop: '6px' }}>Original Selling = Revised Selling Bud value from operation code 90001</div>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #0369a1' }}>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Fore Var $ (Forecast Variance)</div>
                    <code style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>= Orig Bud - FAC</code>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #0369a1' }}>
                    <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Fore Var % (Forecast Variance %)</div>
                    <code style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>= (Orig Bud - FAC) / Orig Bud</code>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#e0f2fe', borderRadius: '8px', borderLeft: '4px solid #0ea5e9' }}>
                    <div style={{ fontWeight: '600', color: '#0369a1', marginBottom: '8px' }}>EVM Metrics (Analytics)</div>
                    <div style={{ fontSize: '12px', color: '#374151', lineHeight: '1.8' }}>
                      <div><strong>PV</strong> (Planned Value) = Sum of Orig Bud</div>
                      <div><strong>AC</strong> (Actual Cost) = Sum of Actual</div>
                      <div><strong>EV</strong> (Earned Value) = Sum of (% Complete × Orig Bud per row)</div>
                      <div><strong>CPI</strong> (Cost Performance Index) = EV / AC</div>
                      <div><strong>SPI</strong> (Schedule Performance Index) = EV / PV</div>
                      <div><strong>CV</strong> (Cost Variance) = EV - AC</div>
                      <div><strong>SV</strong> (Schedule Variance) = EV - PV</div>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#fef3c7', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                    <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>Contract vs Extra (Auto-Calculated)</div>
                    <div style={{ fontSize: '12px', color: '#78350f', lineHeight: '1.8' }}>
                      <div>Operation Code <strong>90001</strong> = Revenue</div>
                      <div>Operation Codes <strong>00001-21999</strong> = Contract</div>
                      <div>Operation Codes <strong>25001-29999</strong> = Extra</div>
                      <div>Operation Codes <strong>81001-89999</strong> = Extra</div>
                      <div>All other codes = (blank)</div>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#dbeafe', borderRadius: '8px', borderLeft: '4px solid #3b82f6', marginTop: '12px' }}>
                    <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>Understanding Revenue Line Items in Maestro</div>
                    <div style={{ fontSize: '12px', color: '#1e3a5f', lineHeight: '1.8' }}>
                      <div>In Maestro, <strong>revenue is split into separate line items per discipline</strong> (e.g., Piping Fabrication, Piping Production, Mechanical, Electrical), rather than appearing as a single consolidated entry.</div>
                      <div style={{ marginTop: '8px' }}>Revenue line items use operation code <strong>90001</strong> with a <strong>suffix</strong> to identify the discipline type.</div>
                      <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#3b82f6' }}>Example: For Job# 71876, the Piping Fabrication Revenue op code would be <strong>90001PFS</strong>, the Piping Revenue op code would be <strong>90001PPS</strong>, etc.</div>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#dcfce7', borderRadius: '8px', borderLeft: '4px solid #22c55e', marginTop: '12px' }}>
                    <div style={{ fontWeight: '600', color: '#166534', marginBottom: '8px' }}>Notes Column - @Mention Feature</div>
                    <div style={{ fontSize: '12px', color: '#14532d', lineHeight: '1.8' }}>
                      <div>You can <strong>mention team members</strong> in notes by typing <strong>@</strong> followed by their email.</div>
                      <div style={{ marginTop: '6px' }}>A dropdown will appear with matching users as you type. Select a user to insert their email.</div>
                      <div style={{ marginTop: '6px' }}>Mentioned users are highlighted in <span style={{ color: '#0066cc', fontWeight: '600', backgroundColor: '#e7f3ff', padding: '1px 4px', borderRadius: '3px' }}>blue</span> for easy visibility.</div>
                      <div style={{ marginTop: '6px' }}><strong>Email notifications</strong> are automatically sent to mentioned users when you save the note.</div>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #64748b', marginTop: '12px' }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>Cell Background Colors</div>
                    <div style={{ fontSize: '12px', color: '#334155', lineHeight: '2' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '16px', backgroundColor: '#86efac', borderRadius: '3px', border: '1px solid #22c55e' }}></span>
                        <strong>Bright Green:</strong> Recently modified cell (5-second highlight)
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '16px', backgroundColor: '#dcfce7', borderRadius: '3px', border: '1px solid #86efac' }}></span>
                        <strong>Light Green:</strong> Editable / Manual entry fields (e.g., % Complete, ETC, Note)
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '16px', backgroundColor: '#fef9c3', borderRadius: '3px', border: '1px solid #fcd34d' }}></span>
                        <strong>Light Cream:</strong> Calculated fields (e.g., FAC, Forecasted Profit, % Margin)
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '16px', backgroundColor: '#e0f2fe', borderRadius: '3px', border: '1px solid #7dd3fc' }}></span>
                        <strong>Light Blue:</strong> Previous period data (e.g., % Complete Prev, ETC Prev, FAC Prev)
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '20px', height: '16px', backgroundColor: '#f3f4f6', borderRadius: '3px', border: '1px solid #d1d5db' }}></span>
                        <strong>Light Gray:</strong> Read-only / Maestro data (e.g., Orig Bud, Actual, Comm Costs)
                      </div>
                      <div style={{ marginTop: '6px', fontStyle: 'italic', color: '#64748b' }}>
                        Rows alternate between lighter and slightly darker shades for better readability (zebra striping).
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Finalize Section */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>5</span>
                  Finalize Period
                </h3>
                <div style={{ backgroundColor: '#fef3c7', borderRadius: '8px', padding: '16px', border: '1px solid #fcd34d' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#92400e' }}>
                    <strong>✓ Finalize</strong> button copies current values to "Previous Period" columns:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400e', fontSize: '13px' }}>
                    <li>% Complete → % Complete Prev</li>
                    <li>ETC → ETC Prev</li>
                    <li>FAC → FAC Prev</li>
                    <li>Revised Selling Bud → Revised Selling Bud Prev</li>
                  </ul>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#78350f' }}>
                    Previous period data becomes read-only. A PDF report is auto-downloaded.
                  </p>
                </div>
              </div>

              {/* Tips Section */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: '#0369a1', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>💡</span>
                  Tips
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#374151', fontSize: '13px', lineHeight: '1.8' }}>
                  <li><strong>Single-click</strong> editable cells (including % Complete and ETC) to enter values — no double-click needed. Zero cells open blank so you can type immediately; existing values are selected so typing replaces them</li>
                  <li>Drag the <strong>blue square</strong> on <strong>% Complete</strong> or <strong>Revised Selling Bud</strong> to copy that value to the rows above or below. Contract rows are skipped for Revised Selling Bud</li>
                  <li><strong>Editable fields:</strong> Contract vs Extra, Revised Bud, % Complete, ETC, Revised Selling Bud, Note</li>
                  <li><strong>Read-only fields:</strong> Operation Code, Cost Type, Activity, Orig Bud, Actual, Comm Costs, Bud Bal, FAC, Forecasted Profit, % Margin, Fore Var $, Fore Var %</li>
                  <li>Set <strong>Contract vs Extra</strong> to "contract" or "extra" to control the Forecasted Profit formula</li>
                  <li>Editing <strong>% Complete</strong> or <strong>ETC</strong> triggers auto-recalculation of FAC, Forecasted Profit, and % Margin</li>
                  <li>The <strong>driver</strong> between <strong>% Complete</strong> and <strong>ETC</strong> — the one you typed — is shown in <strong>bold</strong>; the other one was calculated from it</li>
                  <li>If neither <strong>% Complete</strong> nor <strong>ETC</strong> is entered, <strong>FAC</strong> carries over the original estimated cost (Orig Bud)</li>
                  <li>Use <strong>Import Maestro</strong> to upload Maestro Excel - automatically feeds to Forecasting</li>
                  <li>Click column headers to <strong>sort</strong>; use filter icons to <strong>filter</strong> data</li>
                  <li>Press <strong>ESC</strong> or click outside to close filter dropdowns</li>
                </ul>
              </div>

              {/* Next Steps Section */}
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ backgroundColor: '#7c3aed', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>🔮</span>
                  Next Steps (Planned Improvements)
                </h3>
                <div style={{ backgroundColor: '#faf5ff', borderRadius: '8px', padding: '16px', border: '1px solid #e9d5ff' }}>
                  <ol style={{ margin: 0, paddingLeft: '20px', color: '#581c87', fontSize: '13px', lineHeight: '1.8' }}>
                    <li>
                      <strong>FAC (Forecast at Completion) calculation method — default + per-activity.</strong>{' '}
                      Today one priority sequence (% Complete → ETC → Orig Bud) applies to every row.
                      Planned: pick a <strong>default forecasting method</strong> for the project, and
                      <strong> override the method per activity</strong> where a specific line needs a different approach.
                    </li>
                    <li style={{ opacity: 0.75 }}>
                      <strong>Currency conversion at import (CAD/USD)</strong> — ✅ <strong>done</strong> (Sep 2026).
                      The import now converts each company's dollars to the master project currency, stamps the
                      rate used, and the toolbar has a <strong>View $</strong> toggle.
                    </li>
                  </ol>
                </div>
              </div>
              </>
              )}
              {helpTab === 'tour' && (
                <HelpVideoTour
                  guideId="forecasting-dashboard-help"
                  title="Forecasting Dashboard Help"
                  accent="#0369a1"
                  compact
                  showHeader
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* FINALIZE MODAL */}
      {finalizeModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {/* Modal Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>Finalize Period</h2>
              <button onClick={() => setFinalizeModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Finalization History */}
              {finalizationHistory.length > 0 && (
                <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Recent Finalizations</h4>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    {finalizationHistory.map((h, i) => (
                      <div key={h.id} style={{ padding: '8px 0', borderBottom: i < finalizationHistory.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                        <div style={{ fontWeight: '500', color: '#374151' }}>
                          {new Date(h.finalized_at).toLocaleString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                        <div style={{ fontSize: '12px', marginTop: '2px' }}>
                          by {h.finalized_by || 'Unknown'} | {h.rows_copied || 0} rows{h.pdf_generated ? ' | PDF generated' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 1: Copy Values Question */}
              {finalizeStep === 1 && (
                <div>
                  <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                    Would you like to <strong>copy the current % Complete, ETC, FAC, and Revised Selling Bud values</strong> to their "Previous Period" columns?
                  </p>
                  <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#6b7280' }}>
                    This action preserves your current forecasting values before making new updates. Previous period data cannot be edited after copying.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => setFinalizeStep(2)} 
                      style={{ padding: '10px 20px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                      No, Skip Copy
                    </button>
                    <button 
                      onClick={() => handleFinalize(true, true)} 
                      style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                      Yes, Copy & Generate PDF
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: PDF Only Question */}
              {finalizeStep === 2 && (
                <div>
                  <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                    You chose not to copy values. Would you still like to <strong>generate a PDF report</strong> of the current data?
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => { setFinalizeModalOpen(false); }} 
                      style={{ padding: '10px 20px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                      No, Cancel
                    </button>
                    <button 
                      onClick={() => handleFinalize(false, true)} 
                      style={{ padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                      Yes, Generate PDF Only
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: '6px', marginBottom: '16px' }}>{error}</div>}

      {/* Table with grouped headers, sorting, filtering */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', fontSize: '13px' }}>
        {/* Header — horizontally scrollable and scroll-synced with the row area, so the
            right-hand columns stay reachable even when no rows are loaded. */}
        <div ref={fdHeaderRef} style={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <div style={{ width: fdTotalWidth }}>
            {/* Group header row — forecasting view only */}
            {dashboardView === 'forecasting' && (
              <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6' }}>
                <div style={{ width: 40, minWidth: 40, flexShrink: 0, padding: '6px', backgroundColor: '#495057', position: 'sticky', left: 0, zIndex: 25, borderRight: '1px solid #dee2e6', boxSizing: 'border-box' }} />
                {COLUMN_GROUPS.map(group => {
                  const groupCols = fdVisibleCols.filter(c => c.group === group.id);
                  if (groupCols.length === 0) return null;
                  const groupWidth = groupCols.reduce((sum, c) => sum + (columnWidths[c.key] || c.width), 0);
                  const isFrozenGroup = group.id === 'identification';
                  return (
                    <div key={group.id} style={{
                      width: groupWidth, minWidth: groupWidth, maxWidth: groupWidth, flexShrink: 0,
                      padding: '6px 0',
                      backgroundColor: group.color, color: group.textColor || '#1f2937',
                      textAlign: 'center', fontWeight: '600', fontSize: '11px',
                      textTransform: 'uppercase', letterSpacing: '0.3px',
                      borderLeft: '1px solid #dee2e6', borderRight: '1px solid #dee2e6',
                      position: isFrozenGroup ? 'sticky' : 'relative',
                      left: isFrozenGroup ? 40 : undefined,
                      zIndex: isFrozenGroup ? 25 : 20,
                      boxSizing: 'border-box',
                    }}>
                      {group.label}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Column header row */}
            <div style={{ display: 'flex', backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <div style={{
                width: 40, minWidth: 40, flexShrink: 0,
                padding: '10px 8px',
                borderRight: '1px solid #dee2e6',
                position: 'sticky', left: 0, backgroundColor: '#f8f9fa', zIndex: 25, color: '#495057',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxSizing: 'border-box',
              }}>
                <input type="checkbox"
                  checked={filteredData.length > 0 && selectedRows.size === filteredData.length}
                  onChange={() => { if (selectedRows.size === filteredData.length) setSelectedRows(new Set()); else setSelectedRows(new Set(filteredData.map(r => r.id))); }}
                />
              </div>
              {fdVisibleCols.map(col => {
                const colWidth = columnWidths[col.key] || col.width;
                const frozenLeft = fdFrozenOffsets[col.key];
                const hasFilter = filterTemplate.columnFilters[col.key]?.length > 0 || filterTemplate.filterText[col.key];
                return (
                  <div key={col.key} style={{
                    width: colWidth, minWidth: colWidth, maxWidth: col.frozen ? colWidth : undefined,
                    flexShrink: 0,
                    padding: '10px 8px',
                    borderRight: '1px solid #dee2e6',
                    fontWeight: '600', color: '#495057', whiteSpace: 'nowrap',
                    backgroundColor: '#f8f9fa', cursor: 'pointer',
                    position: col.frozen ? 'sticky' : 'relative',
                    left: frozenLeft,
                    zIndex: col.frozen ? 25 : 20,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                      {/* Long labels are truncated with an ellipsis rather than hard-cut
                          mid-word, and the full text stays available as a tooltip. */}
                      <span
                        onClick={() => handleSort(col.key)}
                        title={col.label}
                        style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {col.label}
                        {sortConfig.key === col.key && (
                          <span style={{ marginLeft: '4px', fontSize: '10px' }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </span>
                      <span onClick={(e) => filterTemplate.openFilterAtPosition(e, col.key)} style={{ cursor: 'pointer', opacity: hasFilter ? 1 : 0.5, display: 'flex' }} title="Filter">
                        <FilterIcon isActive={!!hasFilter} color="#495057" />
                      </span>
                    </div>
                    {/* Resize handle */}
                    <div
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', backgroundColor: 'transparent', borderRight: '2px solid transparent' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderRight = '2px solid #3b82f6'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderRight = '2px solid transparent'; }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {/* Virtualized body — this is the horizontal scroll container; frozen cols use position:sticky */}
        <div ref={listScrollRef}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              No data. {dashboardView === 'maestro' ? 'Use Import → Maestro Import to load data.' : 'Click Add Row or use Import.'}
            </div>
          ) : (
            <List
              listRef={fdListRef}
              rowComponent={FDRowComponent}
              rowCount={filteredData.length}
              rowHeight={FD_ROW_HEIGHT}
              rowProps={{
                filteredData,
                visibleCols: fdVisibleCols,
                frozenOffsets: fdFrozenOffsets,
                columnWidths,
                totalWidth: fdTotalWidth,
                selectedRows,
                setSelectedRows,
                editingCell,
                editValue,
                setEditingCell: updateEditingCell,
                setEditValue,
                handleCellUpdate,
                recentlyModified,
                fillDrag,
                onFillStart: fdConvertedView ? null : handleFillStart,
                readOnlyAll: fdConvertedView,
              }}
              overscanCount={10}
              defaultHeight={Math.min(FD_TABLE_MAX_HEIGHT, filteredData.length * FD_ROW_HEIGHT)}
              style={{ height: Math.min(FD_TABLE_MAX_HEIGHT, filteredData.length * FD_ROW_HEIGHT), overflowX: 'auto' }}
            />
          )}
        </div>

        {/* Subtotal rows — scroll-synced with header */}
        {subtotalMode.size > 0 && subtotalRows.length > 0 && (
          <div ref={fdSubtotalRef} style={{ overflowX: 'hidden', overflowY: 'hidden', borderTop: '3px solid #374151' }}>
            {subtotalRows.map((st, stIdx) => {
              const isOverallTotal = st._subtotalLabel === 'TOTAL';
              const bothActive = subtotalMode.has('overall') && subtotalMode.has('byDiscipline');
              return (
              <div key={stIdx} style={{
                display: 'flex', width: fdTotalWidth, minHeight: FD_ROW_HEIGHT,
                fontWeight: '700', fontSize: '12px',
                borderBottom: '1px solid #dee2e6',
                borderTop: (isOverallTotal && bothActive) ? '3px double #374151' : undefined,
                backgroundColor: (isOverallTotal && bothActive) ? '#f0fdf4' : undefined,
              }}>
                {/* Checkbox placeholder */}
                <div style={{ width: 40, minWidth: 40, flexShrink: 0, position: 'sticky', left: 0, zIndex: 10, backgroundColor: '#d4edda', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid #dee2e6' }}>
                  <span style={{ fontSize: '10px', color: '#1f2937' }}>Σ</span>
                </div>
                {fdVisibleCols.map((col, colIdx) => {
                  const colWidth = columnWidths[col.key] || col.width;
                  const frozenLeft = fdFrozenOffsets[col.key];
                  const isFrozen = frozenLeft !== undefined;
                  let cellBg;
                  if (col.calculated) {
                    cellBg = '#fef9c3';
                  } else if (col.previous) {
                    cellBg = '#e0f2fe';
                  } else if (col.readOnly) {
                    const group = COLUMN_GROUPS.find(g => g.columns.includes(col.key));
                    cellBg = group ? group.color : '#f3f4f6';
                  } else {
                    cellBg = '#dcfce7';
                  }
                  let cellContent;
                  if (colIdx === 0) {
                    cellContent = st._subtotalLabel;
                  } else if (col.type === 'number') {
                    const v = st[col.key];
                    cellContent = v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
                  } else if (col.type === 'percent') {
                    const v = st[col.key];
                    cellContent = v != null ? Number(v).toFixed(1) + '%' : '';
                  } else {
                    cellContent = '';
                  }
                  return (
                    <div key={col.key} style={{
                      width: colWidth, minWidth: colWidth, maxWidth: colWidth, flexShrink: 0,
                      padding: '8px 6px', boxSizing: 'border-box', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      borderRight: '1px solid #dee2e6',
                      textAlign: col.type === 'number' || col.type === 'percent' ? 'right' : 'left',
                      position: isFrozen ? 'sticky' : 'static', left: isFrozen ? frozenLeft : undefined,
                      zIndex: isFrozen ? 10 : 1, backgroundColor: cellBg, color: '#1f2937',
                    }}>
                      {cellContent}
                    </div>
                  );
                })}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Info with subtotal toggle */}
      <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#6b7280', fontSize: '13px' }}>
        <span>{filteredData.length} rows | {currentVisibleColumns.size} of {COLUMNS.length} columns visible</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>Subtotals:</span>
          <button
            onClick={() => setSubtotalMode(prev => { const next = new Set(prev); if (next.has('overall')) next.delete('overall'); else next.add('overall'); return next; })}
            style={{
              padding: '3px 10px', fontSize: '11px', borderRadius: '4px', cursor: 'pointer', border: 'none',
              backgroundColor: subtotalMode.has('overall') ? '#495057' : '#e5e7eb',
              color: subtotalMode.has('overall') ? '#fff' : '#374151',
              fontWeight: subtotalMode.has('overall') ? '600' : '400',
            }}
          >Overall</button>
          <button
            onClick={() => setSubtotalMode(prev => { const next = new Set(prev); if (next.has('byDiscipline')) next.delete('byDiscipline'); else next.add('byDiscipline'); return next; })}
            style={{
              padding: '3px 10px', fontSize: '11px', borderRadius: '4px', cursor: 'pointer', border: 'none',
              backgroundColor: subtotalMode.has('byDiscipline') ? '#495057' : '#e5e7eb',
              color: subtotalMode.has('byDiscipline') ? '#fff' : '#374151',
              fontWeight: subtotalMode.has('byDiscipline') ? '600' : '400',
            }}
          >By Discipline</button>
          <span style={{ marginLeft: '8px' }}>{selectedRows.size} selected</span>
        </div>
      </div>

      {(showColumnConfig || showExportDropdown || showViewDropdown) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}
          onClick={() => { setShowColumnConfig(false); setShowExportDropdown(false); setShowViewDropdown(false); }} />
      )}

      {/* Filtering Template dropdown (reusable module) */}
      {filterTemplate.openFilterColumn && (
        <FilterTemplateDropdown
          columnKey={filterTemplate.openFilterColumn}
          columnLabel={filterTemplate.columnLabels[filterTemplate.openFilterColumn]}
          showTextFilters={true}
          isNumericColumn={filterTemplate.numericColumnKeys.includes(filterTemplate.openFilterColumn)}
          columnFilters={filterTemplate.columnFilters}
          filterMode={filterTemplate.filterMode}
          filterText={filterTemplate.filterText}
          filterSearch={filterTemplate.filterSearch}
          filterButtonPositions={filterTemplate.filterButtonPositions}
          setOpenFilterColumn={filterTemplate.setOpenFilterColumn}
          setFilterSearch={filterTemplate.setFilterSearch}
          getUniqueValues={filterTemplate.getUniqueValues}
          handleFilterToggle={filterTemplate.handleFilterToggle}
          selectAllColumnFilter={filterTemplate.selectAllColumnFilter}
          clearColumnFilter={filterTemplate.clearColumnFilter}
          applyTextFilter={filterTemplate.applyTextFilter}
        />
      )}
      
      {/* Toast Notification for @mention emails */}
      {/* IMPORT DATA MODAL — confirmation / success / error */}
      {importDataModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>

            {/* ── UNFINALIZED WARNING ── */}
            {importDataModal.type === 'confirm-unfinalized' && (<>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>⚠️</span>
                <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#92400e' }}>Forecast Not Finalized</h2>
              </div>
              <div style={{ padding: '24px' }}>
                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                  Your current forecast has <strong>% Complete / ETC entries that were not finalized</strong>. Importing replaces every row with fresh Maestro data.
                </p>
                <div style={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '600', color: '#92400e' }}>Reset by the import (current period):</p>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#78350f', lineHeight: '1.8' }}>
                    <li>% Complete, ETC and FAC (start fresh at 0)</li>
                    <li>Revised Selling Bud overrides</li>
                  </ul>
                  <p style={{ margin: '10px 0 0 0', fontSize: '13px', fontWeight: '600', color: '#166534' }}>
                    ✓ Preserved: the frozen Previous Period columns and Notes (matched by operation code).
                  </p>
                  <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#92400e' }}>
                    Want the <strong>current</strong> values frozen too? Cancel and use <strong>✓ Finalize</strong> first — that snapshots them into the Prev columns, which survive the import.
                  </p>
                </div>
                {renderImportBatchPicker()}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setImportDataModal(null)} style={{ padding: '9px 18px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                    Cancel — Finalize First
                  </button>
                  <button onClick={executeImportData} style={{ padding: '9px 18px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                    Import Anyway
                  </button>
                </div>
              </div>
            </>)}

            {/* ── STANDARD REPLACE CONFIRMATION ── */}
            {importDataModal.type === 'confirm-replace' && (<>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>📥</span>
                <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#111827' }}>Import Maestro Data</h2>
              </div>
              <div style={{ padding: '24px' }}>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                  This will replace all existing forecast rows for this project with the selected Maestro Reports import.
                  The frozen <strong>Previous Period</strong> columns and <strong>Notes</strong> are preserved (matched by operation code).
                </p>
                {renderImportBatchPicker()}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setImportDataModal(null)} style={{ padding: '9px 18px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={executeImportData} style={{ padding: '9px 18px', backgroundColor: '#1d4ed8', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                    Confirm Import
                  </button>
                </div>
              </div>
            </>)}

            {/* ── FIRST IMPORT (empty dashboard) ── */}
            {importDataModal.type === 'confirm-fresh' && (<>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>📥</span>
                <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#111827' }}>Import Maestro Data</h2>
              </div>
              <div style={{ padding: '24px' }}>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
                  Choose which Maestro Reports import to load into the Forecasting Dashboard.
                </p>
                {renderImportBatchPicker()}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setImportDataModal(null)} style={{ padding: '9px 18px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={executeImportData} style={{ padding: '9px 18px', backgroundColor: '#1d4ed8', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                    Import
                  </button>
                </div>
              </div>
            </>)}

            {/* ── SUCCESS ── */}
            {importDataModal.type === 'success' && (<>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>✅</span>
                <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#166534' }}>Import Complete</h2>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{ backgroundColor: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#166534' }}>
                    {importDataModal.count} row{importDataModal.count !== 1 ? 's' : ''} imported successfully
                  </p>
                  {importDataModal.batch && (
                    <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#15803d' }}>
                      From Maestro import of {new Date(importDataModal.batch.imported_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                      {importDataModal.batch.label ? ` — ${importDataModal.batch.label}` : ''}
                    </p>
                  )}
                  {importDataModal.skipped > 0 && (
                    <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#15803d' }}>
                      {importDataModal.skipped} "total" row{importDataModal.skipped !== 1 ? 's' : ''} skipped
                    </p>
                  )}
                  {importDataModal.preservedPrev > 0 && (
                    <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#15803d' }}>
                      ❄️ Frozen Previous Period values restored on {importDataModal.preservedPrev} row{importDataModal.preservedPrev !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                {importDataModal.fx?.applied && (
                  <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#1e3a5f', lineHeight: '1.6' }}>
                      💱 <strong>Currency conversion applied:</strong>{' '}
                      {importDataModal.fx.convertedRows} Maestro row{importDataModal.fx.convertedRows !== 1 ? 's' : ''} converted to{' '}
                      <strong>{importDataModal.fx.masterCurrency}</strong>
                      {Object.entries(importDataModal.fx.usedRates || {}).map(([k, v]) =>
                        ` (${k.replace('_', ' → ')} @ ${Number(v.rate).toFixed(6)})`
                      ).join('')}
                      . All forecast values are now in {importDataModal.fx.masterCurrency}.
                    </p>
                    {importDataModal.fx.unknownPrefixRows > 0 && (
                      <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#b45309' }}>
                        ⚠️ {importDataModal.fx.unknownPrefixRows} row(s) had a prefix with no known division/currency and were imported unconverted.
                      </p>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setImportDataModal(null)} style={{ padding: '9px 20px', backgroundColor: '#166534', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                    Done
                  </button>
                </div>
              </div>
            </>)}

            {/* ── ERROR ── */}
            {importDataModal.type === 'error' && (<>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>❌</span>
                <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#dc2626' }}>Import Failed</h2>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#dc2626' }}>{importDataModal.message}</p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setImportDataModal(null)} style={{ padding: '9px 20px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                    Close
                  </button>
                </div>
              </div>
            </>)}

          </div>
        </div>
      )}

      {/* AUDIT LOG — change history for this job's Forecast + Maestro views */}
      <AuditLogModal
        isOpen={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        endpoint={`/forecasting-dashboard/${jobNumber}/audit-log`}
        title={`Forecasting Audit Log — Job ${jobNumber}`}
        accentColor="#1e3a5f"
      />

      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          backgroundColor: toast.type === 'error' ? '#fef2f2' : '#dcfce7',
          color: toast.type === 'error' ? '#dc3545' : '#166534',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          animation: 'slideInFromRight 0.3s ease'
        }}>
          <span style={{ fontSize: '1.2rem' }}>{toast.type === 'error' ? '✕' : '✓'}</span>
          <span>{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            style={{
              background: 'none',
              border: 'none',
              color: toast.type === 'error' ? '#dc3545' : '#166534',
              cursor: 'pointer',
              fontSize: '1.2rem',
              marginLeft: '0.5rem'
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default ForecastingDashboard1;
